import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Carousel } from "primereact/carousel";
import { getSectionLabel } from "../../constants/sectionOptions";
import { getEmbedUrl } from "../../utils/videoEmbed";
import { useResponsiveScale } from "./ResponsiveScaler";
import {
    RocketLaunchIcon, CameraIcon, LockClosedIcon, EyeIcon, SparklesIcon,
    ComputerDesktopIcon, ChatBubbleLeftRightIcon, BriefcaseIcon, PlayCircleIcon,
    VideoCameraIcon, StarIcon, HeartIcon, PhoneIcon, EnvelopeIcon, MapPinIcon,
    UserGroupIcon, ShieldCheckIcon, BoltIcon, GlobeAltIcon, HomeIcon,
    AcademicCapIcon, ArchiveBoxIcon, ArrowPathIcon, BellIcon, BookmarkIcon,
    BuildingLibraryIcon, BuildingOffice2Icon, BuildingStorefrontIcon,
    CalendarDaysIcon, ChartBarIcon, ChartPieIcon, CheckBadgeIcon, CheckCircleIcon,
    ClockIcon, CloudIcon, CogIcon, CreditCardIcon, CubeIcon, DocumentIcon,
    DocumentTextIcon, FaceSmileIcon, FlagIcon, FolderIcon, GiftIcon,
    HandThumbUpIcon, HashtagIcon, IdentificationIcon, InboxIcon, InformationCircleIcon,
    KeyIcon, LanguageIcon, LightBulbIcon, LinkIcon, ListBulletIcon, MagnifyingGlassIcon,
    MegaphoneIcon, MicrophoneIcon, MoonIcon, MusicalNoteIcon, NewspaperIcon,
    PaperAirplaneIcon, PaperClipIcon, PencilIcon, PhotoIcon, PresentationChartBarIcon,
    PrinterIcon, QrCodeIcon, QuestionMarkCircleIcon, RadioIcon, ScaleIcon,
    ShoppingBagIcon, ShoppingCartIcon, SpeakerWaveIcon, SunIcon, SwatchIcon,
    TagIcon, TicketIcon, TrophyIcon, TruckIcon, TvIcon, UserCircleIcon, UserIcon,
    WifiIcon, XCircleIcon, ArrowDownIcon, ArrowUpIcon, ArrowLeftIcon, ArrowRightIcon,
    ShareIcon, WrenchIcon, WrenchScrewdriverIcon, BeakerIcon, FireIcon, FunnelIcon,
    HandRaisedIcon, MapIcon, PuzzlePieceIcon, RectangleStackIcon, ScissorsIcon,
    TableCellsIcon, VariableIcon, WindowIcon, CommandLineIcon, CircleStackIcon,
    CurrencyRupeeIcon, CurrencyDollarIcon, DevicePhoneMobileIcon, GlobeAmericasIcon,
    NoSymbolIcon, PowerIcon, ServerIcon, SignalIcon, SquaresPlusIcon, StopCircleIcon,
} from "@heroicons/react/24/outline";
import {
    Activity, Airplay, AlertCircle, AlertTriangle, Anchor, Aperture, Award,
    Baby, Banana, Battery, Bike, Bird, Bluetooth, Bold, Book, BookOpen,
    Bookmark, Box, Briefcase as LBriefcase, Building, Building2, Bus,
    Calculator, Car, Cast, CheckSquare, Clipboard, Cloud as LCloud,
    Cloudy, Code, Code2, Coffee, Compass, Cookie, Copy, Cpu, CreditCard as LCreditCard,
    Crown, Database, Delete, Diamond, DollarSign, Download, Droplet, Dumbbell,
    Edit, Edit2, ExternalLink, Eye as LEye, Facebook, Film, Filter,
    Flag as LFlag, Flame, Flower, Flower2, Folder as LFolder, Frown,
    Gift as LGift, Globe, Globe2, Grab, GraduationCap, Grid,
    Hammer, Heart as LHeart, Home as LHome, Hospital, Hotel,
    Image, Inbox, Instagram, Italic, Joystick, Keyboard, Lamp, Landmark,
    Laptop, Layers, Leaf, Library, Link2, Linkedin, List, Lock,
    LogIn, LogOut, Mail, Map, MapPin as LMapPin, Medal, Menu, Merge,
    MessageCircle, MessageSquare, Mic, Minimize, Monitor, Moon as LMoon,
    Mountain, Mouse, Music, Music2, Newspaper, Package, Palette,
    Paperclip, Pencil, PersonStanding, Phone as LPhone, PiggyBank,
    Pin, Pizza, Plane, Plus, Podcast, Printer, QrCode, Quote,
    Radio, Recycle, Ribbon, Rocket, Rss, Save, Scale, Scissors,
    Search, Send, Settings, Share, Share2, Shield, ShoppingBag as LShoppingBag,
    ShoppingCart as LShoppingCart, Shrub, Smile, Snowflake, Star as LStar,
    Stethoscope, Store, Sun, Sunset, Sword, Tag as LTag, Target, Terminal,
    ThumbsDown, ThumbsUp, Timer, Trash, Trees, TreeDeciduous, TreePine,
    Trophy, Truck, Tv, Umbrella, University, Upload, User, Users,
    Video, Wallet, Watch, Wifi as LWifi, Wind, Wrench, X, Zap, ZoomIn,
} from "lucide-react";
import backgroundone from "../../assets/images/background-image-1.jpg";
import backgroundtwo from "../../assets/images/background-image-2.jpg";
import backgroundthree from "../../assets/images/background-image-3.jpg";

export const backgrounds = [backgroundone, backgroundtwo, backgroundthree];

// Icons the admin can place on sections (name → component).
// Heroicon keys: PascalCase name minus "Icon" suffix, lowercased → backward-compat with saved data.
// Lucide keys: PascalCase name lowercased; prefix "l-" when key already exists from heroicons.
export const ICON_MAP: Record<string, React.ComponentType<any>> = {
    // ── Heroicons outline ──────────────────────────────
    rocketlaunch: RocketLaunchIcon, camera: CameraIcon, lockclosed: LockClosedIcon,
    eye: EyeIcon, sparkles: SparklesIcon, computerdesktop: ComputerDesktopIcon,
    chatbubbleleftright: ChatBubbleLeftRightIcon, briefcase: BriefcaseIcon,
    playcircle: PlayCircleIcon, videocamera: VideoCameraIcon, star: StarIcon,
    heart: HeartIcon, phone: PhoneIcon, envelope: EnvelopeIcon, mappin: MapPinIcon,
    usergroup: UserGroupIcon, shieldcheck: ShieldCheckIcon, bolt: BoltIcon,
    globealt: GlobeAltIcon, home: HomeIcon, academiccap: AcademicCapIcon,
    archivebox: ArchiveBoxIcon, arrowpath: ArrowPathIcon, bell: BellIcon,
    bookmark: BookmarkIcon, buildinglibrary: BuildingLibraryIcon,
    buildingoffice2: BuildingOffice2Icon, buildingstorefront: BuildingStorefrontIcon,
    calendardays: CalendarDaysIcon, chartbar: ChartBarIcon, chartpie: ChartPieIcon,
    checkbadge: CheckBadgeIcon, checkcircle: CheckCircleIcon, clock: ClockIcon,
    cloud: CloudIcon, cog: CogIcon, creditcard: CreditCardIcon, cube: CubeIcon,
    document: DocumentIcon, documenttext: DocumentTextIcon, facesmile: FaceSmileIcon,
    flag: FlagIcon, folder: FolderIcon, gift: GiftIcon, handthumbup: HandThumbUpIcon,
    hashtag: HashtagIcon, identification: IdentificationIcon, inbox: InboxIcon,
    informationcircle: InformationCircleIcon, key: KeyIcon, language: LanguageIcon,
    lightbulb: LightBulbIcon, link: LinkIcon, listbullet: ListBulletIcon,
    magnifyingglass: MagnifyingGlassIcon, megaphone: MegaphoneIcon,
    microphone: MicrophoneIcon, moon: MoonIcon, musicalnote: MusicalNoteIcon,
    newspaper: NewspaperIcon, paperairplane: PaperAirplaneIcon, paperclip: PaperClipIcon,
    pencil: PencilIcon, photo: PhotoIcon, presentationchartbar: PresentationChartBarIcon,
    printer: PrinterIcon, qrcode: QrCodeIcon, questionmarkcircle: QuestionMarkCircleIcon,
    radio: RadioIcon, scale: ScaleIcon, shoppingbag: ShoppingBagIcon,
    shoppingcart: ShoppingCartIcon, speakerwave: SpeakerWaveIcon, sun: SunIcon,
    swatch: SwatchIcon, tag: TagIcon, ticket: TicketIcon, trophy: TrophyIcon,
    truck: TruckIcon, tv: TvIcon, usercircle: UserCircleIcon, user: UserIcon,
    wifi: WifiIcon, xcircle: XCircleIcon, arrowdown: ArrowDownIcon, arrowup: ArrowUpIcon,
    arrowleft: ArrowLeftIcon, arrowright: ArrowRightIcon, share: ShareIcon,
    wrench: WrenchIcon, wrenchscrewdriver: WrenchScrewdriverIcon, beaker: BeakerIcon,
    fire: FireIcon, funnel: FunnelIcon, handraised: HandRaisedIcon, map: MapIcon,
    puzzlepiece: PuzzlePieceIcon, rectanglestack: RectangleStackIcon, scissors: ScissorsIcon,
    tablecells: TableCellsIcon, variable: VariableIcon, window: WindowIcon,
    commandline: CommandLineIcon, circlestack: CircleStackIcon,
    currencyrupee: CurrencyRupeeIcon, currencydollar: CurrencyDollarIcon,
    devicephonemobile: DevicePhoneMobileIcon, globeamericas: GlobeAmericasIcon,
    nosymbol: NoSymbolIcon, power: PowerIcon, server: ServerIcon, signal: SignalIcon,
    squaresplus: SquaresPlusIcon, stopcircle: StopCircleIcon,

    // ── Lucide (key = lowercase name; "l-" prefix if heroicon key conflicts) ──
    activity: Activity, airplay: Airplay, alertcircle: AlertCircle,
    alerttriangle: AlertTriangle, anchor: Anchor, aperture: Aperture, award: Award,
    baby: Baby, banana: Banana, battery: Battery, bike: Bike, bird: Bird,
    bluetooth: Bluetooth, bold: Bold, book: Book, bookopen: BookOpen,
    "l-bookmark": Bookmark, box: Box, "l-briefcase": LBriefcase, building: Building,
    building2: Building2, bus: Bus, calculator: Calculator, car: Car, cast: Cast,
    checksquare: CheckSquare, clipboard: Clipboard, "l-cloud": LCloud, cloudy: Cloudy,
    code: Code, code2: Code2, coffee: Coffee, compass: Compass, cookie: Cookie,
    copy: Copy, cpu: Cpu, "l-creditcard": LCreditCard, crown: Crown, database: Database,
    delete: Delete, diamond: Diamond, dollarsign: DollarSign, download: Download,
    droplet: Droplet, dumbbell: Dumbbell, edit: Edit, edit2: Edit2,
    externallink: ExternalLink, "l-eye": LEye, facebook: Facebook, film: Film,
    filter: Filter, "l-flag": LFlag, flame: Flame, flower: Flower, flower2: Flower2,
    "l-folder": LFolder, frown: Frown, "l-gift": LGift, globe: Globe, globe2: Globe2,
    grab: Grab, graduationcap: GraduationCap, grid: Grid, hammer: Hammer,
    "l-heart": LHeart, "l-home": LHome, hospital: Hospital, hotel: Hotel,
    image: Image, "l-inbox": Inbox, instagram: Instagram, italic: Italic,
    joystick: Joystick, keyboard: Keyboard, lamp: Lamp, landmark: Landmark,
    laptop: Laptop, layers: Layers, leaf: Leaf, library: Library, link2: Link2,
    linkedin: Linkedin, list: List, lock: Lock, login: LogIn, logout: LogOut,
    mail: Mail, "l-map": Map, "l-mappin": LMapPin, medal: Medal, menu: Menu,
    merge: Merge, messagecircle: MessageCircle, messagesquare: MessageSquare,
    mic: Mic, minimize: Minimize, monitor: Monitor, "l-moon": LMoon,
    mountain: Mountain, mouse: Mouse, music: Music, music2: Music2,
    "l-newspaper": Newspaper, package: Package, palette: Palette,
    "l-paperclip": Paperclip, "l-pencil": Pencil, personsanding: PersonStanding,
    "l-phone": LPhone, piggybank: PiggyBank, pin: Pin, pizza: Pizza, plane: Plane,
    plus: Plus, podcast: Podcast, "l-printer": Printer, "l-qrcode": QrCode,
    quote: Quote, "l-radio": Radio, recycle: Recycle, ribbon: Ribbon, rocket: Rocket,
    rss: Rss, save: Save, "l-scale": Scale, "l-scissors": Scissors, search: Search,
    send: Send, settings: Settings, "l-share": Share, share2: Share2,
    shield: Shield, "l-shoppingbag": LShoppingBag, "l-shoppingcart": LShoppingCart,
    shrub: Shrub, smile: Smile, snowflake: Snowflake, "l-star": LStar,
    stethoscope: Stethoscope, store: Store, "l-sun": Sun, sunset: Sunset,
    sword: Sword, "l-tag": LTag, target: Target, terminal: Terminal,
    thumbsdown: ThumbsDown, thumbsup: ThumbsUp, timer: Timer, trash: Trash,
    trees: Trees, treedeciduous: TreeDeciduous, treepine: TreePine, "l-trophy": Trophy,
    "l-truck": Truck, "l-tv": Tv, umbrella: Umbrella, university: University,
    upload: Upload, users: Users, video: Video, wallet: Wallet, watch: Watch,
    "l-wifi": LWifi, wind: Wind, "l-wrench": Wrench, x: X, zap: Zap, zoomin: ZoomIn,
};

export const ICON_OPTIONS = Object.keys(ICON_MAP).map((key) => ({
    label: key.charAt(0).toUpperCase() + key.slice(1),
    value: key,
}));

// Resolves a builder section's height: custom px, half screen or full screen.
// Only correct when NOT inside a zoomed (ResponsiveScaler) tree — e.g. the admin's
// unzoomed WYSIWYG editing canvas — since raw vh ignores any ancestor zoom factor.
// For the live site / preview (both zoomed), use useZoomAwareSectionHeight instead.
export const sectionHeight = (section: any): string => {
    if (section?.heightMode === "auto") return "auto";
    if (section?.heightMode === "half") return "50vh";
    if (section?.heightMode === "full") return "100vh";
    return `${section?.minHeight || 300}px`;
};

// Zoom-aware version of sectionHeight for use inside a ResponsiveScaler-wrapped
// tree (live site + admin preview). Everything else in the design (positions,
// widths, font sizes) is defined in the fixed ~1440px design space and just
// zooms down together with the device width — so "half"/"full" height need to
// follow the SAME rule (a fixed design-space px value) rather than the real
// device's own viewport height. Tying height to the real device while width
// stays tied to the design canvas makes the section's aspect ratio balloon on
// a narrow phone (same absolute height, much narrower width) — a wide desktop
// hero becomes a tall, mostly-empty box on mobile. Using desktop-typical
// px approximations keeps the section's proportions consistent at any size,
// exactly like every other px value in the design already does.
export const useZoomAwareSectionHeight = (section: any): string => {
    // Free Style: no declared floor at all — the auto-measure logic below
    // (resolvedMinHeight) sizes the section purely to its actual content.
    if (section?.heightMode === "auto") return "0px";
    if (section?.heightMode === "half") return "450px";
    if (section?.heightMode === "full") return "900px";
    return `${section?.minHeight || 300}px`;
};

// Column count for the fixed template grids (Events, Page Videos) keyed off the
// real effective width rather than a CSS breakpoint — a real `sm:`/`md:` class
// reads the admin's actual (wide) browser window, not the narrow forced-width
// preview frame, and would never collapse to a mobile layout there. This stays
// accurate in both the live site and the admin's Mobile/Tablet preview.
export const useResponsiveColumns = (maxColumns: number): number => {
    const { effectiveWidth } = useResponsiveScale();
    if (effectiveWidth < 640) return 1;
    if (effectiveWidth < 1024) return Math.min(2, maxColumns);
    return maxColumns;
};

// Uniform zoom-scaling shrinks a desktop-tuned font size by the same ratio as
// everything else — fine for proportions, but on a real phone (~390px vs the
// 1440px design, ~0.27x) that turns a comfortable 18px heading into ~5px on
// screen, unreadably small. This inflates the design-space size so the text
// lands closer to `minRealPx` on the real, on-screen (post-zoom) result.
//
// The boost is capped at `maxBoostMultiplier`× the natural (unboosted) zoomed
// size rather than chasing minRealPx unconditionally — text elements are
// absolutely positioned with a fixed maxWidth, and on a very small real scale
// (~0.27x) reaching an absolute px floor can mean tripling the font size,
// which no longer fits the tight margin it was placed in (e.g. a caption
// squeezed next to a photo) and overflows past the section instead. Capping
// the multiplier trades "always exactly minRealPx" for "meaningfully more
// readable without breaking layout".
export const useMinFontSize = (desktopPx: number, minRealPx: number = 14, maxBoostMultiplier: number = 2): number => {
    const { scale } = useResponsiveScale();
    const naturalRealPx = desktopPx * scale;
    if (naturalRealPx >= minRealPx) return desktopPx;
    const cappedRealPx = Math.min(minRealPx, naturalRealPx * maxBoostMultiplier);
    return cappedRealPx / scale;
};

// Same idea as useMinFontSize, but for a freeform image element's width (given as
// a % of the ~1440px design canvas). A photo placed at, say, 25% of the design
// width is ~360px on desktop but shrinks to ~65px on a real phone (~0.18x once
// zoom applies to a 25% width too) — recognizable-photo-sized on desktop,
// postage-stamp-sized on mobile.
//
// Boosts the % only for the portion of the shrink actually caused by zoom —
// preserves at least `minScaleRatio` (e.g. 0.55 = 55%) of however big the admin
// made it AT DESKTOP. Crucially this is a no-op whenever scale >= 1 (desktop —
// there's no zoom shrinkage happening to compensate for at all), so it can never
// second-guess an admin's own small-by-design choice — e.g. a small corner logo
// set to 3% width — the way a flat real-px floor did (that bug: a flat floor
// boosted anything under ~90px real width even at full desktop scale, since it
// had no reference to what the admin actually intended for that element).
export const useMinImageWidthPercent = (designPercent: number, minScaleRatio: number = 0.55): number => {
    const { scale } = useResponsiveScale();
    const floorScale = Math.max(scale, minScaleRatio) / scale; // always exactly 1 at/above desktop scale
    return Math.min(100, designPercent * floorScale);
};

export const SHADOW_OPTIONS = [
    { label: "None", value: "none" },
    { label: "S", value: "sm" },
    { label: "M", value: "md" },
    { label: "L", value: "lg" },
];

const SHADOW_PRESETS: Record<string, { box: string; text: string; icon: string }> = {
    sm: { box: "0 1px 4px rgba(0,0,0,0.25)", text: "1px 1px 2px rgba(0,0,0,0.4)", icon: "drop-shadow(0 1px 2px rgba(0,0,0,0.4))" },
    md: { box: "0 4px 12px rgba(0,0,0,0.35)", text: "2px 2px 4px rgba(0,0,0,0.5)", icon: "drop-shadow(0 2px 4px rgba(0,0,0,0.5))" },
    lg: { box: "0 10px 28px rgba(0,0,0,0.45)", text: "3px 3px 6px rgba(0,0,0,0.6)", icon: "drop-shadow(0 4px 8px rgba(0,0,0,0.6))" },
};

// Maps an element's shadow preset to the right CSS property for its kind:
// box-shadow for box/image/button, text-shadow for text, drop-shadow filter for icons.
export const getShadowStyle = (kind: string | undefined, shadow?: string): React.CSSProperties => {
    if (!shadow || shadow === "none") return {};
    const preset = SHADOW_PRESETS[shadow];
    if (!preset) return {};
    if (kind === "icon") return { filter: preset.icon };
    if (kind === "image" || kind === "box" || kind === "button") return { boxShadow: preset.box };
    return { textShadow: preset.text };
};

// "Box" elements can take any of these shapes, filled with a color or an image.
export const SHAPE_OPTIONS = [
    { label: "Rectangle", value: "rectangle" },
    { label: "Circle", value: "circle" },
    { label: "Triangle", value: "triangle" },
    { label: "Pentagon", value: "pentagon" },
    { label: "Hexagon", value: "hexagon" },
    { label: "Star", value: "star" },
    { label: "Line", value: "line" },
];

export const getShapeClipPath = (shapeType?: string): string | undefined => {
    switch (shapeType) {
        case "triangle":
            return "polygon(50% 0%, 0% 100%, 100% 100%)";
        case "pentagon":
            return "polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)";
        case "hexagon":
            return "polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)";
        case "star":
            return "polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)";
        default:
            return undefined; // rectangle/circle use border-radius instead
    }
};

// Fill (color or image), border and corner/clip shaping shared by the editor canvas and the live site
export const shapeFillStyle = (el: any): React.CSSProperties => {
    const clipPath = getShapeClipPath(el.shapeType);
    const isCircle = el.shapeType === "circle";
    return {
        background: el.url ? undefined : el.bg || "#ffffff",
        borderRadius: isCircle ? "50%" : clipPath ? undefined : `${el.radius ?? 8}px`,
        clipPath,
        border: el.borderWidth ? `${el.borderWidth}px solid ${el.borderColor || "#000000"}` : undefined,
        overflow: el.url ? "hidden" : undefined,
        ...getShadowStyle("box", el.shadow),
    };
};

export const builderElementStyle = (el: any): React.CSSProperties => ({
    position: "absolute",
    left: `${el.x}%`,
    top: `${el.y}%`,
    fontSize: `${el.fontSize || 16}px`,
    color: el.color || "#ffffff",
    fontWeight: el.bold ? 700 : 400,
    fontStyle: el.italic ? "italic" : "normal",
    whiteSpace: "pre-wrap",
    // Capped to whatever space is actually left from this element's own x position
    // to the section's right edge (up to 80% as a sane cap for text placed near
    // the left) — a flat 80% let text positioned near the edge (e.g. a caption at
    // x:83%, with only ~17% left) claim far more width than physically available
    // before wrapping, so it overflowed the section instead of wrapping in time.
    maxWidth: `${Math.min(80, 100 - (el.x || 0))}%`,
    ...getShadowStyle("text", el.shadow),
});

// Renders one placed element (text, image, icon, box or button) read-only.
// When el.link is set the element becomes a link: /local paths open in the same
// tab, http(s) URLs open in a new tab.
//
// fixedSize: for header/footer chrome. These are dense, precisely-spaced
// regions (e.g. "Privacy" and "Terms" links positioned a few % apart, sized to
// just barely fit side by side at their original size) — boosting any one
// element's size for legibility grows it past the gap the admin spaced it into,
// overlapping its neighbor. Renders it at its true, as-designed desktop pixel
// size on any device instead — never shrinking illegibly small, but also never
// growing past the spacing the admin actually set up.
// Unset height (the default) follows the image's own aspect ratio. An
// explicit px height crops via object-fit instead of stretching/distorting
// it. An explicit % height means "% of the image's own rendered width" —
// aspect-ratio expresses that natively regardless of how width itself was
// determined (section %, or a constant-px group), no JS pixel math needed.
// Matches the admin editor's identical helper.
const imageHeightStyle = (el: any, chromeTier = 1): React.CSSProperties => {
    if (!el.height) return { height: "auto" };
    if (el.heightUnit === "%") {
        return { aspectRatio: `100 / ${el.height}`, height: "auto", objectFit: "cover" };
    }
    return { height: `${el.height * chromeTier}px`, objectFit: "cover" };
};

export const ElementView: React.FC<{ el: any; fixedSize?: boolean; staticFlow?: boolean; constantSize?: boolean; wrapText?: boolean; revealStyle?: React.CSSProperties }> = ({ el, fixedSize, staticFlow, constantSize, wrapText, revealStyle }) => {
    // Called unconditionally up front (rules of hooks) — only the branch that
    // matches el.kind actually uses its value below. Design-tuned px sizes get
    // inflated just enough to stay legible/visible once real device zoom shrinks
    // everything proportionally (see useMinFontSize's comment for why that matters).
    const textFontSize = useMinFontSize(el.fontSize || 16, 13);
    const buttonFontSize = useMinFontSize(el.fontSize || 16, 13);
    const iconSize = useMinFontSize(el.size || 48, 24);
    // Called unconditionally either way (rules of hooks) but only actually used
    // for non-line boxes below — a "Line" shape's whole point is being a thin
    // hairline, not something that needs boosting up to a "readable" minimum
    // like a badge/button would. Without this exception a 2-3px line render
    // 2x+ thicker live than in the editor (which never applies this boost),
    // the same class of editor/live mismatch as the header/footer text strut.
    const boostedBoxHeight = useMinFontSize(el.height || 100, 40);
    const boxHeight = el.shapeType === "line" ? (el.height || 100) : boostedBoxHeight;
    // Clamped so the boost never pushes the image past the right edge of its
    // section (these are absolutely positioned by el.x%, and the section clips
    // overflow) — never shrinks below the admin's own configured width though.
    const imageWidthPercent = Math.max(el.width || 30, Math.min(useMinImageWidthPercent(el.width || 30), 100 - (el.x || 0)));
    const { scale, effectiveWidth } = useResponsiveScale();
    const counterZoom = 1 / scale;
    // Chrome (header/footer) shrinks across explicit breakpoint tiers, same as
    // the rest of the site, instead of staying a constant desktop size — it just
    // does so via true real-pixel values (counter-zoomed below) instead of
    // relying on ambient zoom, since boosting/zooming it the normal way is what
    // caused it to overlap its own tightly-spaced neighbors in the first place.
    // constantSize (e.g. a grouped photo) skips the breakpoint reduction
    // entirely — truly the same size everywhere, not just less-variable.
    const chromeTier = constantSize ? 1 : effectiveWidth < 480 ? 0.65 : effectiveWidth < 768 ? 0.8 : effectiveWidth < 1024 ? 0.9 : 1;

    // staticFlow: header/footer stacked on mobile — rendered as a normal-flow
    // flex item (in reading order) instead of absolutely positioned by x/y%,
    // since x/y was tuned for elements sitting side by side on one line and
    // doesn't translate to a stacked column.
    const base: React.CSSProperties = staticFlow
        ? { position: "static" }
        : { position: "absolute", left: `${el.x}%`, top: `${el.y}%` };

    if (fixedSize) {
        // The OUTER element carries only the position (left/top %, resolved
        // against the ambient zoom exactly like every other element) and is
        // NEVER itself zoomed. CSS zoom scales an element's own rendering as a
        // unit — applying it to the SAME element that also carries left/top
        // would risk scaling that position offset too, fighting the percentage
        // placement. So the counter-zoom instead goes on an INNER wrapper: its
        // content is specified in true desktop px (× the breakpoint tier above),
        // and since ambient zoom × this inner counter-zoom = 1, those px values
        // land as real, final on-screen pixels on any device.
        let inner: React.ReactNode;
        if (el.kind === "image") {
            // Unset width = natural/intrinsic size, no override. Explicit px
            // is a literal (counter-zoomed) pixel width. Explicit % (the
            // default once set) resolves against the fixed 1440px design
            // canvas, same as always.
            const fixedWidthStyle = !el.width
                ? "auto"
                : el.widthUnit === "px"
                    ? `${el.width * chromeTier}px`
                    : `${(el.width / 100) * 1440 * chromeTier}px`;
            inner = (
                <img
                    src={el.url}
                    alt=""
                    style={{
                        width: fixedWidthStyle,
                        display: "block",
                        ...imageHeightStyle(el, chromeTier),
                        ...getShadowStyle("image", el.shadow),
                    }}
                />
            );
        } else if (el.kind === "icon") {
            const Icon = ICON_MAP[el.icon] || StarIcon;
            const size = (el.size || 48) * chromeTier;
            inner = <Icon style={{ width: `${size}px`, height: `${size}px`, color: el.color || "#ffffff", ...getShadowStyle("icon", el.shadow) }} />;
        } else if (el.kind === "box") {
            const boxWidthPx = ((el.width || 20) / 100) * 1440 * chromeTier;
            const boxHeightPx = (el.height || 100) * chromeTier;
            inner = (
                <div style={{ width: `${boxWidthPx}px`, height: `${boxHeightPx}px`, ...shapeFillStyle(el), transform: el.rotation ? `rotate(${el.rotation}deg)` : undefined }}>
                    {el.url && <img src={el.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />}
                </div>
            );
        } else if (el.kind === "button") {
            const BtnIcon = el.icon ? ICON_MAP[el.icon] : null;
            const iconPx = ((el.fontSize || 16) + 2) * chromeTier;
            const btnIconNode = BtnIcon ? <BtnIcon style={{ width: `${iconPx}px`, height: `${iconPx}px`, flexShrink: 0 }} /> : null;
            inner = (
                <div
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: btnIconNode ? "8px" : undefined,
                        background: el.bg || "#22c55e",
                        color: el.color || "#ffffff",
                        padding: "10px 24px",
                        borderRadius: `${el.radius ?? 8}px`,
                        fontSize: `${(el.fontSize || 16) * chromeTier}px`,
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        ...getShadowStyle("button", el.shadow),
                    }}
                >
                    {el.iconPos !== "right" && btnIconNode}
                    {el.text || "Button"}
                    {el.iconPos === "right" && btnIconNode}
                </div>
            );
        } else {
            const maxWidthPx = Math.min(80, 100 - (el.x || 0)) * 14.4; // % of the 1440px design canvas, as real px
            inner = (
                <span
                    style={{
                        display: "inline-block",
                        fontSize: `${(el.fontSize || 16) * chromeTier}px`,
                        color: el.color || "#ffffff",
                        fontWeight: el.bold ? 700 : 400,
                        fontStyle: el.italic ? "italic" : "normal",
                        whiteSpace: "pre-wrap",
                        maxWidth: `${maxWidthPx}px`,
                        ...getShadowStyle("text", el.shadow),
                    }}
                >
                    {el.text}
                </span>
            );
        }

        // fontSize: 0 on both wrapper levels — neither sets its own font size,
        // so without this they inherit whatever ambient font-size sits above
        // them (e.g. the page body default), and a browser reserves a line-box
        // "strut" for an inline-block sized off THAT inherited font, not the
        // smaller explicit size actually set on the inner text span. That phantom
        // strut silently offset every fixedSize text element (header/footer)
        // vertically by an amount that scaled with the gap between the two font
        // sizes — invisible normally, but glaring next to a plain box/shape
        // element (e.g. a divider line) positioned at the same y%, since a box
        // has no such strut to begin with. Zeroing it here removes the strut
        // entirely, so a text element's rendered position finally matches its
        // declared y% the same way every other element kind already does.
        const wrapped = <span style={{ display: "inline-block", zoom: counterZoom, fontSize: 0 }}>{inner}</span>;

        // staticFlow elements (header/footer stacking, or an image-caption group's
        // inner pieces) aren't independently section-positioned any more, so
        // they're excluded from the closed-form height measurement below — either
        // their parent stacked section doesn't use it at all, or (image-caption
        // groups) the group wrapper itself is the correct thing to measure.
        const measurementProps = staticFlow ? {} : { "data-el-item": true, "data-el-y": el.y || 0 };

        if (el.link) {
            const external = /^https?:\/\//i.test(el.link);
            return (
                <a
                    href={el.link}
                    target={external ? "_blank" : undefined}
                    rel={external ? "noreferrer" : undefined}
                    {...measurementProps}
                    style={{ ...base, textDecoration: "none", cursor: "pointer", fontSize: 0 }}
                >
                    {wrapped}
                </a>
            );
        }
        // No link → plain arrow cursor, so only genuinely clickable (linked)
        // elements ever invite a click with the pointer cursor.
        return <span {...measurementProps} style={{ ...base, cursor: "default", fontSize: 0 }}>{wrapped}</span>;
    }

    let style: React.CSSProperties = base;
    let node: React.ReactNode = null;

    if (el.kind === "image") {
        // Unset width = natural/intrinsic size (the mobile-legibility %
        // boost below doesn't apply — boosting a deliberately natural-sized
        // image contradicts the point of leaving it unset). Explicit px is
        // literal. Explicit % (the default once set, matching every
        // pre-existing stored value) keeps the existing boosted percentage.
        // The wrapper carries the resolved size; a percentage child of an
        // auto-width wrapper doesn't resolve, so "auto" needs to be on the
        // inner <img> too, not the usual 100%.
        const widthStyle = !el.width ? "auto" : el.widthUnit === "px" ? `${el.width}px` : `${imageWidthPercent}%`;
        const innerWidthStyle = widthStyle === "auto" ? "auto" : "100%";
        style = { ...base, width: widthStyle, display: "block", ...getShadowStyle("image", el.shadow) };
        node = (
            <img
                src={el.url}
                alt=""
                style={{
                    width: innerWidthStyle,
                    display: "block",
                    ...imageHeightStyle(el),
                }}
            />
        );
    } else if (el.kind === "icon") {
        const Icon = ICON_MAP[el.icon] || StarIcon;
        style = { ...base, lineHeight: 0, display: "block" };
        node = (
            <Icon
                style={{ width: `${iconSize}px`, height: `${iconSize}px`, color: el.color || "#ffffff", ...getShadowStyle("icon", el.shadow) }}
            />
        );
    } else if (el.kind === "box") {
        style = {
            ...base,
            display: "block",
            width: `${el.width || 20}%`,
            height: `${boxHeight}px`,
            ...shapeFillStyle(el),
            transform: el.rotation ? `rotate(${el.rotation}deg)` : undefined,
        };
        if (el.url) {
            node = <img src={el.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />;
        }
    } else if (el.kind === "button") {
        const BtnIcon = el.icon ? ICON_MAP[el.icon] : null;
        const btnIconNode = BtnIcon ? (
            <BtnIcon style={{ width: `${buttonFontSize + 2}px`, height: `${buttonFontSize + 2}px`, flexShrink: 0 }} />
        ) : null;
        style = {
            ...base,
            display: "inline-flex",
            alignItems: "center",
            gap: btnIconNode ? "8px" : undefined,
            background: el.bg || "#22c55e",
            color: el.color || "#ffffff",
            padding: "10px 24px",
            borderRadius: `${el.radius ?? 8}px`,
            fontSize: `${buttonFontSize}px`,
            fontWeight: 600,
            whiteSpace: "nowrap",
            ...getShadowStyle("button", el.shadow),
        };
        node = (
            <>
                {el.iconPos !== "right" && btnIconNode}
                {el.text || "Button"}
                {el.iconPos === "right" && btnIconNode}
            </>
        );
    } else if (staticFlow) {
        // Two different staticFlow text uses need opposite wrapping behavior:
        // a grouped caption (image-caption group) is a short one-liner that
        // must stay on one line at its natural width (see the truncation bug
        // this fixed — a section-relative maxWidth % resolving against the
        // much narrower group wrapper instead of the whole section). A
        // stacked-mobile paragraph (stackContent below) is the opposite: full
        // body text that MUST wrap within the full-width flow column it's now
        // in, or it would render as one unbroken, overflowing line.
        style = {
            position: "static",
            fontSize: `${textFontSize}px`,
            color: el.color || "#ffffff",
            fontWeight: el.bold ? 700 : 400,
            fontStyle: el.italic ? "italic" : "normal",
            whiteSpace: wrapText ? "pre-wrap" : "nowrap",
            width: wrapText ? "100%" : undefined,
            ...getShadowStyle("text", el.shadow),
        };
        node = el.text;
    } else if (el.kind === "text" && (el.role === "title" || el.role === "subtitle")) {
        // Section title/subtitle text (pagevideo/event headers) was hand-
        // positioned by the admin dragging it until it LOOKED centered at
        // desktop width — a plain left-anchored el.x%, no different from any
        // other draggable text. That only "looks" centered for the one exact
        // rendered width it was tuned at: useMinFontSize forces the font
        // larger (relative to a pure proportional zoom) on narrow screens, so
        // the text box grows wider from that same fixed left edge and drifts
        // off-center on mobile/tablet. True centering — anchored at 50%,
        // pulled back by half its own width — stays centered at any width,
        // any font size, unlike a fixed left offset.
        style = {
            position: "absolute",
            left: "50%",
            top: `${el.y}%`,
            transform: "translateX(-50%)",
            textAlign: "center",
            fontSize: `${textFontSize}px`,
            color: el.color || "#ffffff",
            fontWeight: el.bold ? 700 : 400,
            fontStyle: el.italic ? "italic" : "normal",
            whiteSpace: "pre-wrap",
            maxWidth: "90%",
            ...getShadowStyle("text", el.shadow),
        };
        node = el.text;
    } else {
        style = { ...builderElementStyle(el), fontSize: `${textFontSize}px` };
        node = el.text;
    }

    // See the fixedSize branch above for why staticFlow elements are excluded
    // from the closed-form height measurement.
    const measurementProps = staticFlow ? {} : { "data-el-item": true, "data-el-y": el.y || 0 };

    if (el.link) {
        const external = /^https?:\/\//i.test(el.link);
        return (
            <a
                href={el.link}
                target={external ? "_blank" : undefined}
                rel={external ? "noreferrer" : undefined}
                {...measurementProps}
                style={{ ...style, ...revealStyle, textDecoration: "none", cursor: "pointer" }}
            >
                {node}
            </a>
        );
    }
    // No link → plain arrow cursor (see the fixedSize branch above)
    return <span {...measurementProps} style={{ ...style, ...revealStyle, cursor: "default" }}>{node}</span>;
};

// Image background; when fixed, it stays in place while the page scrolls (parallax)
export type ScrollAnimation = "none" | "fade" | "slide" | "slide-down" | "slide-left" | "slide-right" | "zoom" | "flip";

// Reveal-once-scrolled-into-view, shared by the cover image and whole-section
// animation below so both use the exact same trigger logic. IntersectionObserver
// disconnects after firing once — a one-time entrance, not a repeat-on-every-
// scroll effect. `active=false` renders as already-visible immediately (used
// for animation "none", and disabled toggles) with no observer overhead.
//
// useLayoutEffect (not useEffect) for the "already in view on mount" check:
// it runs before the browser paints, so when that check finds the element
// already on screen, React resolves straight to the final visible=true
// render before anything is ever drawn. With useEffect, the FIRST paint
// always happened at the initial opacity:0 state (useEffect runs after
// paint) and only THEN flipped to visible — a real, visible flash-from-
// invisible for anything already on screen at load, not a genuine entrance.
function useScrollReveal<T extends HTMLElement>(active: boolean, externalRef?: React.MutableRefObject<T | null>) {
    const ownRef = useRef<T | null>(null);
    // Lets a caller that already has a ref on the element it wants observed
    // (e.g. BuilderSection's contentSectionRef, already on the <section>
    // itself) reuse it instead of this hook creating an unrelated ref that
    // would need its own extra wrapper div — which, for a section whose
    // children are all position:absolute, would break their percentage
    // top/left (they'd resolve against that new wrapper's own near-zero
    // height instead of the real section).
    const ref = externalRef ?? ownRef;
    const [visible, setVisible] = useState(!active);

    useLayoutEffect(() => {
        if (!active) {
            setVisible(true);
            return;
        }
        const node = ref.current;
        if (!node) return;
        const rect = node.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
            setVisible(true);
            return;
        }
        setVisible(false);
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.1 }
        );
        observer.observe(node);

        // Safety net for a parent still mid-transition at mount (e.g. a
        // Dialog's own open animation) — the synchronous check above can read
        // a transient/incorrect geometry in that case, and while the observer
        // should self-correct once things settle, this guarantees it: one
        // re-check shortly after mount, well past any typical open-transition
        // duration, directly re-measuring rather than trusting the observer's
        // first callback alone.
        const settleTimer = window.setTimeout(() => {
            if (!node.isConnected) return;
            const settledRect = node.getBoundingClientRect();
            if (settledRect.top < window.innerHeight && settledRect.bottom > 0) {
                setVisible(true);
                observer.disconnect();
            }
        }, 400);

        return () => {
            observer.disconnect();
            window.clearTimeout(settleTimer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    return { ref, visible };
}

// The actual per-type CSS for a reveal — shared by the cover image, whole-
// section, and per-element (StaggeredReveal) animation so "Fade"/"Slide"/
// "Zoom"/etc always look the same wherever they're used. delayMs staggers a
// group of elements revealing "one by one" instead of all at once — each
// gets a slightly later transition-delay based on its position. durationSec
// is the admin-configurable Section Animation "seconds" field (defaults 0.7).
const scrollRevealStyle = (animation: ScrollAnimation | undefined, visible: boolean, delayMs = 0, durationSec = 0.7): React.CSSProperties => {
    if (!animation || animation === "none") return {};
    const transition = `opacity ${durationSec}s ease-out ${delayMs}ms, transform ${durationSec}s ease-out ${delayMs}ms`;
    if (animation === "fade") return { transition, opacity: visible ? 1 : 0 };
    if (animation === "slide") return { transition, opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(40px)" };
    if (animation === "slide-down") return { transition, opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(-40px)" };
    if (animation === "slide-left") return { transition, opacity: visible ? 1 : 0, transform: visible ? "translateX(0)" : "translateX(60px)" };
    if (animation === "slide-right") return { transition, opacity: visible ? 1 : 0, transform: visible ? "translateX(0)" : "translateX(-60px)" };
    if (animation === "zoom") return { transition, opacity: visible ? 1 : 0, transform: visible ? "scale(1)" : "scale(0.92)" };
    if (animation === "flip") return { transition, opacity: visible ? 1 : 0, transform: visible ? "perspective(800px) rotateX(0deg)" : "perspective(800px) rotateX(-35deg)" };
    return {};
};

// Ties an element's reveal DIRECTLY to scroll position instead of a one-time
// triggered CSS transition. Used ONLY when a section has an active cover
// image: the section's entrance already IS the natural document-flow scroll
// that slides it up to cover the sticky image (see SectionCoverImage), so a
// separate, disconnected fixed-duration transition on top of that looked
// wrong/uncoupled — this makes the chosen animation track scroll 1:1
// instead. Sections with no cover image keep the normal triggered-once
// scrollRevealStyle above.
function useScrollProgress<T extends HTMLElement>(active: boolean, externalRef?: React.MutableRefObject<T | null>) {
    const ownRef = useRef<T | null>(null);
    const ref = externalRef ?? ownRef;
    const [progress, setProgress] = useState(1);

    useEffect(() => {
        if (!active) {
            setProgress(1);
            return;
        }
        let raf = 0;
        const update = () => {
            raf = 0;
            const node = ref.current;
            if (!node) return;
            const rect = node.getBoundingClientRect();
            // 0 when the element's top is at the bottom of the viewport
            // (just entering), 1 once its top has scrolled up to 25% of the
            // viewport height (settled) — a short, snappy travel distance so
            // it reads as an entrance, not a slow drag across the whole page.
            const vh = window.innerHeight || 800;
            const start = vh;
            const end = vh * 0.25;
            const p = (start - rect.top) / (start - end);
            setProgress(Math.max(0, Math.min(1, p)));
        };
        const onScroll = () => {
            if (raf) return;
            raf = requestAnimationFrame(update);
        };
        update();
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);
        return () => {
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);
            if (raf) cancelAnimationFrame(raf);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    return { ref, progress };
}


// Scroll-linked counterpart to scrollRevealStyle — no `transition` at all
// (the browser doesn't animate it, WE do by re-rendering every scroll
// frame), opacity/transform computed directly from a continuous 0-1
// progress value instead of jumping between two fixed end states.
// progressOffset staggers a group the same way delayMs does for the
// triggered version, but as "needs more scroll before catching up" instead
// of "waits longer before starting".
const scrollLinkedStyle = (animation: ScrollAnimation | undefined, progress: number, progressOffset = 0): React.CSSProperties => {
    if (!animation || animation === "none") return {};
    const p = Math.max(0, Math.min(1, progress - progressOffset));
    const opacity = p;
    if (animation === "fade") return { opacity };
    if (animation === "slide") return { opacity, transform: `translateY(${(1 - p) * 40}px)` };
    if (animation === "slide-down") return { opacity, transform: `translateY(${(1 - p) * -40}px)` };
    if (animation === "slide-left") return { opacity, transform: `translateX(${(1 - p) * 60}px)` };
    if (animation === "slide-right") return { opacity, transform: `translateX(${(1 - p) * -60}px)` };
    if (animation === "zoom") return { opacity, transform: `scale(${0.92 + p * 0.08})` };
    if (animation === "flip") return { opacity, transform: `perspective(800px) rotateX(${(1 - p) * -35}deg)` };
    return {};
};

// Direction-driven entrance for the WHOLE content block — deliberately a much
// bigger horizontal throw (220px) than scrollLinkedStyle's subtle 60px, so a
// left/right pick reads as content genuinely sliding in from off to the side
// (like the slider), not a small per-line nudge. Vertical stays at the same
// 40px scrollLinkedStyle already used for the long-standing, already-correct
// Bottom-to-Top/Top-to-Bottom feel.
const directionScrollStyle = (direction: ScrollAnimation | undefined, progress: number, progressOffset = 0): React.CSSProperties => {
    if (!direction || direction === "none") return {};
    const p = Math.max(0, Math.min(1, progress - progressOffset));
    const opacity = p;
    if (direction === "slide") return { opacity, transform: `translateY(${(1 - p) * 40}px)` };
    if (direction === "slide-down") return { opacity, transform: `translateY(${(1 - p) * -40}px)` };
    if (direction === "slide-left") return { opacity, transform: `translateX(${(1 - p) * 220}px)` };
    if (direction === "slide-right") return { opacity, transform: `translateX(${(1 - p) * -220}px)` };
    return {};
};

// Combines Section Animation (effect TYPE — fade/zoom/flip/slide-*) with the
// independent Scroll Direction field (WHICH edge content enters from) — the
// two are stored separately (see Section.sectionAnimation / .contentDirection
// in HomeContentPage.tsx) so neither dropdown ever overwrites the other's
// value. When a direction is chosen it decides the translate axis (as ONE
// throw, no per-element stagger offset — see StaggeredReveal, which passes
// progressOffset=0 in that case so the whole content moves as a single
// block instead of fragmenting line by line); Section Animation still
// supplies the opacity fade either way, and still applies its own transform
// (zoom/flip/etc) when no direction is chosen.
const contentScrollStyle = (
    sectionAnimation: ScrollAnimation | undefined,
    contentDirection: ScrollAnimation | undefined,
    progress: number,
    progressOffset = 0
): React.CSSProperties => {
    const directionActive = !!contentDirection && contentDirection !== "none";
    if (directionActive) return directionScrollStyle(contentDirection, progress, progressOffset);
    return scrollLinkedStyle(sectionAnimation, progress, progressOffset);
};

// Makes a LIST of elements (a section's text/image/icon/etc components)
// reveal "one by one" instead of all at once — a single shared
// IntersectionObserver (via sectionRef, reusing the caller's own ref on the
// actual section rather than introducing a new wrapper div — see
// useScrollReveal's comment on why a new wrapper would break absolutely-
// positioned children) decides WHEN the group enters view, and each child
// gets an increasing transition-delay based on its position so they cascade
// in. Renders no DOM of its own — just the (possibly re-styled) children —
// so it's a drop-in replacement for rendering the array directly.
export const StaggeredReveal: React.FC<{
    animation?: ScrollAnimation;
    // Independent direction override — see contentScrollStyle. Only ever set
    // (from the admin's Scroll Direction dropdown) when scrollLinked is true.
    direction?: ScrollAnimation;
    staggerMs?: number;
    durationSec?: number;
    // True when the section has an active cover image — switches from a
    // one-time triggered transition to a continuous scroll-linked one (see
    // useScrollProgress above) so the reveal tracks the same scroll that's
    // sliding this section up to cover the sticky image, instead of playing
    // as a separate disconnected animation.
    scrollLinked?: boolean;
    // True when the section itself is position:sticky (see BuilderSection's
    // content-type render) — its own getBoundingClientRect().top freezes once
    // stuck, so progress has to come from useStickyScrollProgress's
    // window.scrollY baseline instead of the rect-based useScrollProgress.
    sticky?: boolean;
    // Sticky's pinned `top` offset (matches the section's own inline style) —
    // useStickyScrollProgress needs this to know when the section has
    // actually reached its stuck position, not just "some scroll happened".
    stickyOffset?: number;
    sectionRef?: React.MutableRefObject<HTMLElement | null>;
    children: React.ReactNode[];
}> = ({ animation, direction, staggerMs = 100, durationSec, scrollLinked, sticky, stickyOffset, sectionRef, children }) => {
    const directionActive = !!scrollLinked && !!direction && direction !== "none";
    const active = (!!animation && animation !== "none") || directionActive;
    const triggered = useScrollReveal<HTMLElement>(!scrollLinked && active, sectionRef);
    const linkedRect = useScrollProgress<HTMLElement>(!!scrollLinked && !sticky && active, sectionRef);
    const linkedSticky = useStickyScrollProgress<HTMLElement>(!!scrollLinked && !!sticky && active, sectionRef, stickyOffset);
    const linked = sticky ? linkedSticky : linkedRect;
    if (!active) return <>{children}</>;
    return (
        <>
            {React.Children.map(children, (child, i) => {
                if (!React.isValidElement(child)) return child;
                const computed = scrollLinked
                    // Direction-driven: whole content moves as ONE block (no
                    // per-element progress offset) — a fragmented per-line
                    // stagger reads as noise for a real left/right slide-in,
                    // unlike the existing bottom/top stagger which already
                    // looks right and stays untouched below.
                    ? contentScrollStyle(animation, direction, linked.progress, directionActive ? 0 : i * 0.15)
                    : scrollRevealStyle(animation, triggered.visible, i * staggerMs, durationSec);
                // Set BOTH: a plain wrapper <div> reads `style` directly, while
                // ElementView (which computes its own style internally and
                // ignores an incoming `style` prop) reads the dedicated
                // `revealStyle` prop instead — this way the same clone works
                // for either kind of child without needing to know which one
                // it got.
                return React.cloneElement(child as React.ReactElement<{ style?: React.CSSProperties; revealStyle?: React.CSSProperties }>, {
                    style: { ...(child.props as any)?.style, ...computed },
                    revealStyle: computed,
                });
            })}
        </>
    );
};

// Scroll-progress for a `position:sticky` element specifically — useScrollProgress
// above measures the element's OWN getBoundingClientRect(), which is exactly
// wrong here: once a sticky element is actually stuck, its rect.top FREEZES at
// the pinned offset and stops changing as the page keeps scrolling, so
// progress would reach some value and then never move again. Instead this
// tracks window.scrollY against a baseline — but the baseline can't just be
// "whenever this hook first runs" (effectively page-load time): for a
// section way down the page, that gives it a huge head start and the reveal
// finishes long before the section is anywhere near actually pinned. So the
// baseline is only captured once the element's rect.top has actually reached
// stickyOffset (i.e. the moment it's about to stick/is stuck) — before that,
// progress stays 0 (fully hidden), exactly matching when the pin visually
// engages.
function useStickyScrollProgress<T extends HTMLElement>(active: boolean, externalRef?: React.MutableRefObject<T | null>, stickyOffset = 0) {
    const ownRef = useRef<T | null>(null);
    const ref = externalRef ?? ownRef;
    const [progress, setProgress] = useState(1);
    const baselineRef = useRef<number | null>(null);
    // Read via a ref inside update(), not the stickyOffset param directly —
    // the effect below only depends on [active], so a plain closure over the
    // param would freeze whatever stickyOffset was at effect-setup time
    // (often still 0, before the header-height measurement lands) and never
    // see it settle to its real value.
    const stickyOffsetRef = useRef(stickyOffset);
    stickyOffsetRef.current = stickyOffset;

    useEffect(() => {
        if (!active) {
            setProgress(1);
            return;
        }
        baselineRef.current = null;
        let raf = 0;
        const TRAVEL = 300; // px of scroll to go from 0 to fully settled
        const update = () => {
            raf = 0;
            const node = ref.current;
            if (!node) return;
            if (baselineRef.current === null) {
                if (node.getBoundingClientRect().top > stickyOffsetRef.current + 1) {
                    setProgress(0);
                    return;
                }
                baselineRef.current = window.scrollY;
            }
            const p = (window.scrollY - baselineRef.current) / TRAVEL;
            setProgress(Math.max(0, Math.min(1, p)));
        };
        const onScroll = () => {
            if (raf) return;
            raf = requestAnimationFrame(update);
        };
        update();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            window.removeEventListener("scroll", onScroll);
            if (raf) cancelAnimationFrame(raf);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    return { ref, progress };
}

// Full-width lead image shown above a section's own content (toggle:
// section.coverImageEnabled) — separate from Bg Image, which paints BEHIND
// content with no animation. coverImageAnimation defaults to "slide" (fade +
// slide up) to match this feature's original behavior before the animation
// type became choosable. Scroll-linked (useScrollProgress), same as the
// content that covers it — a one-time triggered transition only played once
// on entry and then sat still, which didn't read as "this image is also
// moving on the same scroll" the way the content next to it clearly does.
export const SectionCoverImage: React.FC<{ section: any }> = ({ section }) => {
    const url = section?.coverImageUrl;
    const enabled = section?.coverImageEnabled !== false;
    const animation: ScrollAnimation = section?.coverImageAnimation || "slide";
    // The site header is ALSO sticky at top:0 (see HomeContentView.tsx) — a
    // cover image also pinned at top:0 would stick UNDER it, getting its top
    // sliver permanently covered/clipped by the header instead of visible.
    // Measure the header's real (possibly admin-resized) height and pin the
    // image just below it instead. Re-measures on resize since the header's
    // height responds to viewport width (see SiteHeaderBar's breakpoints).
    const [headerOffset, setHeaderOffset] = useState(0);
    useLayoutEffect(() => {
        const measure = () => {
            const header = document.querySelector(".sticky");
            setHeaderOffset(header ? header.getBoundingClientRect().height : 0);
        };
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, []);
    const { ref, progress } = useStickyScrollProgress<HTMLImageElement>(!!url && enabled && animation !== "none", undefined, headerOffset);

    if (!url || !enabled) return null;

    return (
        <img
            ref={ref}
            src={url}
            alt=""
            className="w-full"
            style={{
                // Pinned just below the sticky header while the section's own
                // content (which follows it in normal flow, with its own
                // opaque background and a higher z-index — see each call
                // site) scrolls up and progressively covers it — the image
                // never scrolls away on its own, it gets overwritten by
                // what comes after it.
                position: "sticky",
                top: headerOffset,
                zIndex: 0,
                display: "block",
                maxHeight: "480px",
                objectFit: "cover",
                ...scrollLinkedStyle(animation, progress),
            }}
        />
    );
};

// Wraps an entire section (background + content together) so it fades/slides/
// zooms in the first time it's scrolled into view — section.sectionAnimation,
// defaults to "none" (this is an opt-in feature, unlike the cover image which
// already animated by default before this control existed).
export const ScrollRevealSection: React.FC<{ animation?: ScrollAnimation; direction?: ScrollAnimation; durationSec?: number; scrollLinked?: boolean; children: React.ReactNode }> = ({ animation, direction, durationSec, scrollLinked, children }) => {
    const directionActive = !!scrollLinked && !!direction && direction !== "none";
    const active = (!!animation && animation !== "none") || directionActive;
    const triggered = useScrollReveal<HTMLDivElement>(!scrollLinked && active);
    const linked = useScrollProgress<HTMLDivElement>(!!scrollLinked && active);
    if (!active) return <>{children}</>;
    const ref = scrollLinked ? linked.ref : triggered.ref;
    const style = scrollLinked
        ? contentScrollStyle(animation, direction, linked.progress)
        : scrollRevealStyle(animation, triggered.visible, 0, durationSec);
    return (
        <div ref={ref} style={style}>
            {children}
        </div>
    );
};

export const SectionBgImage: React.FC<{ url: string; fixed?: boolean; overlay?: boolean }> = ({ url, fixed, overlay }) => (
    <>
        {fixed ? (
            <div
                className="absolute top-0 left-0 w-full h-full"
                style={{
                    backgroundImage: `url(${url})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundAttachment: "fixed",
                }}
            ></div>
        ) : (
            <img src={url} alt="" className="absolute top-0 left-0 w-full h-full object-cover" />
        )}
        {overlay && <div className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-50"></div>}
    </>
);

// "FIRST rest…" heading — first word plain, remainder green (matches the site style)
export const SplitHeading: React.FC<{ title: string; light?: boolean }> = ({ title, light }) => {
    const words = title.trim().split(/\s+/);
    const first = words.shift();
    const fontSize = useMinFontSize(36, 22);
    return (
        <h2 className={`font-bold mb-4 ${light ? "text-white" : ""}`} style={{ fontSize }}>
            {first}
            {words.length > 0 && <> <span className="text-green-500">{words.join(" ")}</span></>}
        </h2>
    );
};

export type VideoBox = { x: number; y: number; width: number; height: number };

export const LiveVideoSection: React.FC<{
    videos: any[];
    title?: string;
    videoBox?: VideoBox;
    sectionHeightPx?: string;
}> = ({ videos, title, videoBox, sectionHeightPx }) => {
    const videoTitleSize = useMinFontSize(20, 16);
    if (!videos.length) return null;

    if (videoBox) {
        const fillTemplate = (video: any) => (
            <div className="w-full h-full">
                <VideoEmbed link={video.link} title={video.title || "Live Video"} fill />
            </div>
        );
        return (
            <section className="relative w-full overflow-hidden" style={{ height: sectionHeightPx || "420px", background: "#111827" }}>
                <div className="absolute top-0 left-0 right-0 text-center px-4 pt-6" style={{ pointerEvents: "none" }}>
                    <SplitHeading title={title || "LIVE VIDEO"} light />
                </div>
                <div style={{ position: "absolute", left: `${videoBox.x}%`, top: `${videoBox.y}%`, width: `${videoBox.width}%`, height: `${videoBox.height}px` }}>
                    {videos.length > 1 ? (
                        <Carousel
                            className="live-video-box-carousel"
                            value={videos}
                            itemTemplate={fillTemplate}
                            numVisible={1}
                            numScroll={1}
                            circular
                            showIndicators
                            showNavigators
                        />
                    ) : (
                        fillTemplate(videos[0])
                    )}
                </div>
            </section>
        );
    }

    const template = (video: any) => (
        <div className="max-w-3xl mx-auto px-2">
            {video.title && <h3 className="font-semibold text-white mb-4" style={{ fontSize: videoTitleSize }}>{video.title}</h3>}
            <VideoEmbed link={video.link} title={video.title || "Live Video"} />
        </div>
    );
    return (
        <section className="bg-gray-900 py-16 px-10">
            <div className="max-w-7xl mx-auto text-center">
                <div className="mb-10">
                    <SplitHeading title={title || "LIVE VIDEO"} light />
                </div>
                {videos.length > 1 ? (
                    <Carousel
                        value={videos}
                        itemTemplate={template}
                        numVisible={1}
                        numScroll={1}
                        circular
                        showIndicators
                        showNavigators
                    />
                ) : (
                    template(videos[0])
                )}
            </div>
        </section>
    );
};

// Shared "View More / View Less" pill used by the grid layouts below
const ViewMoreButton: React.FC<{ expanded: boolean; onClick: () => void }> = ({ expanded, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        style={{
            marginTop: "28px",
            padding: "10px 30px",
            borderRadius: "9999px",
            border: "1.5px solid #264164",
            background: expanded ? "#fff" : "#264164",
            color: expanded ? "#264164" : "#fff",
            fontSize: "15px",
            fontWeight: 600,
            cursor: "pointer",
        }}
    >
        {expanded ? "View Less" : "View More"}
    </button>
);

const GRID_INITIAL_COUNT = 6;

// Title/subtitle are no longer rendered here — they're real draggable text
// elements the parent (BuilderSection, or the editor's own canvas) overlays
// above this grid/carousel, so admin can move/style/delete them like any
// other text.
export const PageVideosSection: React.FC<{
    videos: any[];
    displayMode?: "grid" | "slider";
    slideCols?: number;
    cardBg?: string;
    showCardTitle?: boolean;
    cardTitleColor?: string;
}> = ({ videos, displayMode, slideCols, cardBg, showCardTitle = true, cardTitleColor }) => {
    const maxColumns = useResponsiveColumns(3);
    const cardTitleSize = useMinFontSize(18, 15);
    const [showAll, setShowAll] = useState(false);
    if (!videos.length) return null;

    const renderCard = (video: any) => (
        <div key={video._id} className="rounded-lg shadow-md p-4" style={{ background: cardBg || "#f9fafb", flex: "0 1 380px" }}>
            {showCardTitle && (
                <h3 className="font-semibold mb-3 mt-0" style={{ fontSize: cardTitleSize, color: cardTitleColor || undefined }}>
                    {getSectionLabel(video.page)}
                </h3>
            )}
            <VideoEmbed link={video.link} title={getSectionLabel(video.page)} />
        </div>
    );

    const isSlider = displayMode === "slider" && videos.length > 1;
    const visibleVideos = isSlider || showAll ? videos : videos.slice(0, GRID_INITIAL_COUNT);

    // Don't reserve empty grid cells when there are fewer videos than the
    // width-appropriate column count would otherwise allow.
    const columns = Math.min(visibleVideos.length, maxColumns);
    // The desktop-oriented max-width cap (stops 1-2 videos stretching too wide on
    // a full 1440px canvas) is a small slice of that same canvas — on the single-
    // column mobile case it left the card narrow with dead space on both sides.
    // Fill the available width there instead.
    const gridMaxWidth = columns === 1 ? "100%" : `${columns * 380 + (columns - 1) * 32}px`;
    // Admin's chosen cards-per-slide (1-4), still capped to the width-appropriate
    // max columns so it never crams e.g. 4 across on a phone.
    const sliderVisible = Math.min(videos.length, maxColumns, Math.max(1, Math.min(4, slideCols || 3)));

    return (
        <section className="py-16 px-10">
            <div className="max-w-7xl mx-auto text-center">
                {isSlider ? (
                    // key={sliderVisible}: PrimeReact's Carousel only reads numVisible
                    // into its internal state ONCE at mount (React.useState(props.numVisible))
                    // and otherwise only re-syncs it via its OWN responsiveOptions-driven
                    // resize recalculation — a plain prop change on an already-mounted
                    // Carousel is silently ignored (admin picks "1" but it keeps showing
                    // whatever it mounted with, e.g. 3). Forcing a remount whenever the
                    // admin's chosen count changes is the reliable fix.
                    <Carousel
                        key={sliderVisible}
                        value={videos}
                        itemTemplate={(video: any) => <div style={{ padding: "0 16px" }}>{renderCard(video)}</div>}
                        numVisible={sliderVisible}
                        numScroll={1}
                        circular
                        showIndicators
                        showNavigators={false}
                        autoplayInterval={5000}
                    />
                ) : (
                    <>
                        {/* flex-wrap, not CSS grid — grid leaves an incomplete
                            last row's cards stuck at the left instead of
                            centered (e.g. 3 cards at 2 columns: row 2's lone
                            card sat left with dead space beside it). Flex-wrap
                            + justify-content:center handles that naturally. */}
                        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "32px", maxWidth: gridMaxWidth, margin: "0 auto" }}>
                            {visibleVideos.map(renderCard)}
                        </div>
                        {videos.length > GRID_INITIAL_COUNT && (
                            <ViewMoreButton expanded={showAll} onClick={() => setShowAll((p) => !p)} />
                        )}
                    </>
                )}
            </div>
        </section>
    );
};

// "HH:mm" (24-hour, from the admin's <input type="time">) -> "08:00 AM" / "02:30 PM".
const formatEventTime = (time24?: string) => {
    if (!time24) return "";
    const [hStr, mStr] = time24.split(":");
    const h = parseInt(hStr, 10);
    if (isNaN(h)) return time24;
    const period = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${String(h12).padStart(2, "0")}:${mStr || "00"} ${period}`;
};

// One event card, exactly as the home page renders it — exported so the
// admin's "manage events" dialog can show a true preview of each card.
export const EventCard: React.FC<{
    ev: any;
    cardBg?: string;
    showCardTitle?: boolean;
    cardTitleColor?: string;
}> = ({ ev, cardBg, showCardTitle = true, cardTitleColor }) => {
    const monthSize = useMinFontSize(12, 11);
    const daySize = useMinFontSize(24, 19);
    const cardTitleSize = useMinFontSize(18, 15);
    const metaSize = useMinFontSize(14, 12);
    const descriptionSize = useMinFontSize(14, 12);
    // The badge BOX was a flat 68px design-space size with no floor of its own —
    // only the text inside it was protected from shrinking. On mobile the box
    // shrank to ~18px while its (correctly boosted) text didn't shrink to match,
    // so the text overflowed its own badge. Give the box the same treatment.
    const badgeSize = useMinFontSize(68, 50);
    const d = ev.event_date ? new Date(ev.event_date) : null;
    return (
        <div className="rounded-lg shadow-md p-6 text-left" style={{ borderTop: "4px solid #264164", background: cardBg || "#f9fafb" }}>
            {d && (
                <div
                    className="inline-flex flex-col items-center justify-center rounded-lg mb-3"
                    style={{ width: badgeSize, height: badgeSize, background: "#264164", color: "#ffffff", boxShadow: "0 4px 10px rgba(38, 65, 100, 0.35)" }}
                >
                    <span className="font-bold tracking-wide leading-none" style={{ fontSize: monthSize }}>
                        {d.toLocaleDateString("en-IN", { month: "short" }).toUpperCase()}
                    </span>
                    <span className="font-extrabold leading-none mt-1" style={{ fontSize: daySize }}>{d.getDate()}</span>
                </div>
            )}
            {showCardTitle && (
                <h3 className="font-semibold mb-1 mt-0" style={{ fontSize: cardTitleSize, color: cardTitleColor || undefined }}>
                    {ev.name}
                </h3>
            )}
            <p className="text-gray-500 m-0" style={{ fontSize: metaSize }}>
                {ev.event_time ? formatEventTime(ev.event_time) : ""}{ev.event_time && ev.venue ? " · " : ""}{ev.venue || ""}
            </p>
            {ev.description && <p className="text-gray-600 mt-2 mb-0" style={{ fontSize: descriptionSize }}>{ev.description}</p>}
        </div>
    );
};

// Title/subtitle are no longer rendered here — see PageVideosSection's
// identical note; they're overlaid separately as draggable text elements.
export const EventsSection: React.FC<{
    events: any[];
    displayMode?: "grid" | "slider";
    slideCols?: number;
    cardBg?: string;
    showCardTitle?: boolean;
    cardTitleColor?: string;
}> = ({ events, displayMode, slideCols, cardBg, showCardTitle = true, cardTitleColor }) => {
    // Called unconditionally, before the early return below (rules of hooks).
    const maxColumns = useResponsiveColumns(3);
    const { effectiveWidth } = useResponsiveScale();
    const [showAll, setShowAll] = useState(false);

    if (!events.length) return null;

    const isSlider = displayMode === "slider" && events.length > 1;
    const visibleEvents = isSlider || showAll ? events : events.slice(0, GRID_INITIAL_COUNT);
    // Admin's chosen cards-per-slide (1-4), still capped to the width-appropriate
    // max columns so it never crams e.g. 4 across on a phone.
    const sliderVisible = Math.min(events.length, maxColumns, Math.max(1, Math.min(4, slideCols || 3)));

    // Fixed 3-column grid left 1-2 event cards stranded with a big empty gap on
    // wide screens. Match the column count to what's actually there (up to the
    // width-appropriate max) so a couple of upcoming events sit centered as a
    // properly filled row instead, and mobile always stacks to a single column.
    const columns = Math.min(visibleEvents.length, maxColumns);
    const cardMaxWidth = 360;
    // Same desktop-oriented cap issue as PageVideosSection: on the single-column
    // mobile case this fixed cap is a narrow slice of the design canvas, leaving
    // the card stranded with dead space either side instead of filling the width.
    const gridMaxWidth = columns === 1 ? "100%" : `${columns * cardMaxWidth + (columns - 1) * 32}px`;
    // A fixed 64px/40px (py-16 px-10) desktop padding reads as a huge empty
    // border once the section itself is much narrower on mobile/tablet.
    const sectionPadding = effectiveWidth < 640 ? "40px 16px" : effectiveWidth < 1024 ? "50px 24px" : "64px 40px";

    return (
        <section style={{ padding: sectionPadding }}>
            <div className="max-w-7xl mx-auto text-center">
                {isSlider ? (
                    // key={sliderVisible}: see PageVideosSection's identical Carousel
                    // for why this remount is required — PrimeReact ignores a plain
                    // numVisible prop change on an already-mounted Carousel.
                    <Carousel
                        key={sliderVisible}
                        value={events}
                        itemTemplate={(ev: any) => <div style={{ padding: "0 16px" }}><EventCard ev={ev} cardBg={cardBg} showCardTitle={showCardTitle} cardTitleColor={cardTitleColor} /></div>}
                        numVisible={sliderVisible}
                        numScroll={1}
                        circular
                        showIndicators
                        showNavigators={false}
                        autoplayInterval={5000}
                    />
                ) : (
                    <>
                        {/* flex-wrap, not CSS grid — see PageVideosSection's
                            identical fix: grid leaves an incomplete last row's
                            cards stuck at the left instead of centered. */}
                        <div
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                justifyContent: "center",
                                gap: "32px",
                                maxWidth: gridMaxWidth,
                                margin: "0 auto",
                            }}
                        >
                            {visibleEvents.map((ev) => (
                                <div key={ev._id} style={{ flex: `0 1 ${cardMaxWidth}px` }}>
                                    <EventCard ev={ev} cardBg={cardBg} showCardTitle={showCardTitle} cardTitleColor={cardTitleColor} />
                                </div>
                            ))}
                        </div>
                        {events.length > GRID_INITIAL_COUNT && (
                            <ViewMoreButton expanded={showAll} onClick={() => setShowAll((p) => !p)} />
                        )}
                    </>
                )}
            </div>
        </section>
    );
};

export const VideoEmbed: React.FC<{ link: string; title: string; fill?: boolean }> = ({ link, title, fill }) => {
    if (fill) {
        return (
            <iframe
                src={getEmbedUrl(link)}
                title={title}
                className="w-full h-full rounded-lg"
                style={{ border: 0 }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
            />
        );
    }
    return (
        <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
            <iframe
                src={getEmbedUrl(link)}
                title={title}
                className="absolute top-0 left-0 w-full h-full rounded-lg"
                style={{ border: 0 }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
            />
        </div>
    );
};

const SlideView: React.FC<{ slide: any; section: any }> = ({ slide, section }) => {
    const height = useZoomAwareSectionHeight(section);
    // Heuristic for "this slide is just one full-bleed banner image, not a
    // small overlay graphic on a background": full width, anchored at the top.
    const elements = slide.elements || [];
    const bannerImage = elements.find((el: any) => el.kind === "image" && (el.width || 0) >= 95 && (el.y || 0) === 0);

    if (bannerImage) {
        // A fixed slide height + overflow:hidden was cropping the banner
        // whenever its natural aspect ratio (at 100% width) came out taller
        // than that fixed box. Rendering it as a normal, in-flow <img> (rather
        // than through ElementView's always-absolutely-positioned rendering)
        // lets it dictate the slide's actual height itself — shown completely,
        // at its own natural proportions, with nothing cropping or
        // letterboxing it. Any other elements (e.g. an overlay button) still
        // layer on top via their own absolute positioning.
        const overlayElements = elements.filter((el: any) => el !== bannerImage);
        return (
            <div className="relative w-full">
                <img src={bannerImage.url} alt="" style={{ width: "100%", height: "auto", display: "block" }} />
                {overlayElements.map((el: any) => (
                    <ElementView key={el.id} el={el} />
                ))}
            </div>
        );
    }

    return (
        <div className="relative w-full overflow-hidden" style={{ height }}>
            {slide.bgColor ? (
                <div className="absolute top-0 left-0 w-full h-full" style={{ background: slide.bgColor }}></div>
            ) : (
                <SectionBgImage
                    url={slide.bgUrl || backgrounds[(slide.bgIndex || 0) % backgrounds.length]}
                    fixed={slide.bgFixed}
                    overlay
                />
            )}
            {elements.map((el: any) => (
                <ElementView key={el.id} el={el} />
            ))}
        </div>
    );
};

// PrimeReact's Carousel lays every slide (all rendered simultaneously, side by
// side in a flex row, shifted via transform so only one shows) out as flex
// items — by default a flex row's height is the MAX of all its children's
// natural heights, not just the visible one's. With slides now free to be any
// natural (uncropped) height (see SlideView's in-flow <img>), a shorter slide
// ends up sitting inside a box stretched tall enough for the TALLEST slide,
// leaving blank space below its own image before the indicator dots. Fix this
// from the outside rather than fighting Carousel's internal flex layout:
// measure the currently ACTIVE slide's own real rendered height and clip the
// items viewport (a PrimeReact "pt" override, not a global CSS change) to
// exactly that — updated on load, on resize, and whenever the slide changes.
const SliderCarousel: React.FC<{ slides: any[]; section: any }> = ({ slides, section }) => {
    const carouselRef = useRef<any>(null);
    const [activeHeight, setActiveHeight] = useState<number | null>(null);

    // Reading .p-carousel-item-active's OWN scrollHeight is a trap: that element
    // is itself the flex child being stretched (see comment above) — its box has
    // already been forced to the row's tallest-sibling height by the time we
    // measure it, so scrollHeight just reports that same inflated number back,
    // never the shorter real content. The one thing NOT stretched is the actual
    // content SlideView rendered inside it (its first child) — a plain block
    // element that only ever takes its own natural height regardless of what its
    // stretched ancestor's box height is. Measure that instead.
    const measure = () => {
        const root: HTMLElement | undefined = carouselRef.current?.getElement?.();
        const active = root?.querySelector<HTMLElement>(".p-carousel-item-active");
        const content = active?.firstElementChild as HTMLElement | null;
        if (!content) return;
        // Skip a premature FIRST measurement while the active slide's own
        // image is still loading — before it loads, an <img> with no
        // width/height attribute renders at ~0 height, so this would lock
        // activeHeight to a too-small value that then visibly grows once the
        // real load event re-measures a moment later (the "delay" this is
        // fixing). Once a real measurement has landed once, later calls
        // (resize, slide change) always go through — a resize/page-change
        // remeasure legitimately reflects the current box, incomplete image
        // or not.
        if (activeHeight === null) {
            const img = content.querySelector("img");
            if (img && !(img as HTMLImageElement).complete) return;
        }
        setActiveHeight(content.scrollHeight);
    };

    // Re-measuring only needs a listener on images that are STILL loading —
    // returns a cleanup that removes exactly the ones it attached, so callers
    // (mount, and every page change below) can call this repeatedly without
    // piling up duplicate listeners on already-settled images.
    const attachLoadListeners = () => {
        const root: HTMLElement | undefined = carouselRef.current?.getElement?.();
        const imgs = root ? Array.from(root.querySelectorAll("img")) : [];
        const pending = imgs.filter((img) => !(img as HTMLImageElement).complete);
        pending.forEach((img) => img.addEventListener("load", measure));
        return () => pending.forEach((img) => img.removeEventListener("load", measure));
    };

    useEffect(() => {
        const raf = requestAnimationFrame(measure);
        const detach = attachLoadListeners();
        window.addEventListener("resize", measure);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", measure);
            detach();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slides]);

    // Switching slides can bring a not-yet-loaded image into view (Carousel
    // renders every slide up front, but a browser may still defer/stagger
    // when it actually fetches each one) — the mount-time listeners above
    // only cover images that existed and were incomplete at THAT moment.
    // Without this, changing to a slide whose image loads late left the
    // clipped-height box frozen at the previous (wrong) measurement, reading
    // as "the image never finished loading" even though it eventually did.
    const handlePageChange = () => {
        requestAnimationFrame(measure);
        attachLoadListeners();
    };

    return (
        <section className="relative">
            <Carousel
                ref={carouselRef}
                value={slides}
                itemTemplate={(slide: any) => <SlideView slide={slide} section={section} />}
                numVisible={1}
                numScroll={1}
                circular={slides.length > 1}
                autoplayInterval={slides.length > 1 ? 5000 : 0}
                showIndicators={slides.length > 1}
                showNavigators={false}
                onPageChange={handlePageChange}
                pt={{
                    itemsContent: {
                        style: {
                            height: activeHeight != null ? `${activeHeight}px` : "auto",
                            // Before the first real measurement lands, an
                            // unloaded banner <img> (no width/height
                            // attribute, so no known intrinsic size) renders
                            // at 0 height — "auto" then collapses the WHOLE
                            // slider to a 0px sliver until the image finishes
                            // loading, reading as "the slider/image never
                            // loaded" rather than "still loading". A sensible
                            // placeholder floor keeps the section's space
                            // reserved so it just fills in, not pops in.
                            minHeight: activeHeight == null ? "300px" : undefined,
                            transition: "height 0.3s ease",
                        },
                    },
                }}
            />
        </section>
    );
};

// pagevideo/event sections are always created with a hardcoded "#ffffff" Bg
// Color (there's no "unset" state in the data model) — treat that specific
// value as "admin hasn't customized this yet" and fall back to the page's
// own background, so the section blends into the page instead of showing a
// literal white block regardless of what Page Bg is set to. The moment the
// admin repicks a different color (including white again), it sticks.
export const resolveSectionBg = (sectionBg: string | undefined, pagebg: string | undefined): string =>
    sectionBg && sectionBg !== "#ffffff" ? sectionBg : (pagebg || "#ffffff");

export const BuilderSection: React.FC<{ section: any; liveVideos: any[]; pageVideos: any[]; events: any[]; fixedElementSizes?: boolean; pagebg?: string }> = ({
    section,
    liveVideos,
    pageVideos,
    events,
    fixedElementSizes,
    pagebg,
}) => {
    // Called unconditionally before any of the early returns below (rules of hooks).
    const height = useZoomAwareSectionHeight(section);
    const { scale, effectiveWidth } = useResponsiveScale();
    const contentSectionRef = useRef<HTMLElement>(null);
    const [autoHeight, setAutoHeight] = useState<number | null>(null);
    // Same measurement as SectionCoverImage's headerOffset — when this
    // section itself becomes position:sticky (see hasCoverImage below), it
    // needs to pin just below the sticky site header, not under it.
    const [contentHeaderOffset, setContentHeaderOffset] = useState(0);
    useLayoutEffect(() => {
        const measure = () => {
            const header = document.querySelector(".sticky");
            setContentHeaderOffset(header ? header.getBoundingClientRect().height : 0);
        };
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, []);
    // Mobile/tablet hamburger menu (header chrome only — see stackChrome below).
    // The drawer/backdrop render through a PORTAL to document.body: inside the
    // zoomed (and, in the admin preview, transformed) tree, position:fixed
    // coordinates get rescaled unpredictably — outside it they're plain real
    // pixels. drawerRect is the real viewport box the drawer may cover: the
    // preview's phone frame, or the whole window on the live site.
    const [menuOpen, setMenuOpen] = useState(false);
    const [drawerRect, setDrawerRect] = useState<{ top: number; left: number; width: number; height: number; radius: number } | null>(null);
    const drawerScrollerRef = useRef<HTMLElement | null>(null);

    const findScrollerAncestor = (): HTMLElement | null => {
        let node: HTMLElement | null = contentSectionRef.current?.parentElement || null;
        while (node && node !== document.body) {
            const st = window.getComputedStyle(node);
            if (/(auto|scroll)/.test(st.overflowY)) return node;
            node = node.parentElement;
        }
        return null;
    };

    const measureDrawerRect = () => {
        const scroller = drawerScrollerRef.current;
        if (scroller && document.contains(scroller)) {
            // Inset to the frame's INNER (content) box: getBoundingClientRect
            // includes the thick bezel border, and a drawer drawn over that
            // sticks out past the frame visually. clientLeft/Top skip the
            // border, clientWidth/Height exclude border + scrollbar. A small
            // radius keeps the drawer's corners tucked inside the rounded bezel.
            const r = scroller.getBoundingClientRect();
            setDrawerRect({
                top: r.top + scroller.clientTop,
                left: r.left + scroller.clientLeft,
                width: scroller.clientWidth,
                height: scroller.clientHeight,
                radius: 18,
            });
        } else {
            setDrawerRect({ top: 0, left: 0, width: window.innerWidth, height: window.innerHeight, radius: 0 });
        }
    };

    const toggleMenu = () => {
        if (menuOpen) {
            setMenuOpen(false);
            return;
        }
        drawerScrollerRef.current = findScrollerAncestor();
        measureDrawerRect();
        setMenuOpen(true);
    };

    // Keep the drawer glued to its frame: if anything shifts the layout while
    // it's open (window resize, a scroll that slipped through, the admin
    // switching preview mode which resizes the frame), re-measure instead of
    // showing the drawer at a stale position.
    useEffect(() => {
        if (!menuOpen) return;
        const remeasure = () => measureDrawerRect();
        window.addEventListener("resize", remeasure);
        window.addEventListener("scroll", remeasure, true);
        return () => {
            window.removeEventListener("resize", remeasure);
            window.removeEventListener("scroll", remeasure, true);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [menuOpen]);

    // While the drawer is open NOTHING behind it may scroll — lock the frame
    // (admin preview), and the page body always. Longhand only: writing the
    // `overflow` SHORTHAND would wipe the scroller's own inline overflowY:auto
    // (set via React's style prop), which React never re-applies.
    useEffect(() => {
        if (!menuOpen) return;
        const targets: HTMLElement[] = [];
        if (drawerScrollerRef.current) targets.push(drawerScrollerRef.current);
        targets.push(document.body);
        const prev = targets.map((t) => t.style.overflowY);
        targets.forEach((t) => { t.style.overflowY = "hidden"; });
        // Hiding overflow just removed the frame's scrollbar, widening its
        // inner (client) box — the rect measured at open time was ~15px too
        // narrow, leaving a scrollbar-wide gap to the drawer's right. Measure
        // again now that the final geometry is in place.
        const raf = requestAnimationFrame(measureDrawerRect);
        return () => {
            cancelAnimationFrame(raf);
            targets.forEach((t, i) => { t.style.overflowY = prev[i]; });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [menuOpen]);

    // Solves for the section height a closed-form way instead of the naive
    // "measure then resize" approach, which is unstable here: elements are
    // positioned by top:y% of the section's OWN height, so growing the section
    // to fit a measurement shifts every child's position too, which then needs
    // a *different* height — a circular dependency with no guaranteed
    // convergence (it previously over- or under-shot depending on timing).
    // But an element's OWN rendered height never depends on the section's
    // height (only its width, which is % of the section — stable regardless of
    // section height) — so for element i: top_i = y_i% * H, and it needs
    // H such that y_i%*H + ownHeight_i <= H, i.e. H >= ownHeight_i / (1 - y_i%).
    // Solving that directly for every element and taking the max gives the
    // right height in one measurement pass, no iteration or oscillation.
    useEffect(() => {
        const measure = () => {
            const container = contentSectionRef.current;
            if (!container) return;
            let required = 0;
            container.querySelectorAll<HTMLElement>("[data-el-item]").forEach((child) => {
                const yPercent = parseFloat(child.dataset.elY || "0") / 100;
                if (yPercent >= 1) return; // can't solve (would need infinite height)
                const neededHeight = child.offsetHeight / (1 - yPercent);
                if (neededHeight > required) required = neededHeight;
            });
            setAutoHeight(required);
        };
        // rAF: measure once this render's layout (fonts/zoom/wrap) has settled.
        const raf = requestAnimationFrame(measure);
        // Re-measure once web fonts finish loading — text metrics (and therefore
        // wrap points/heights) can shift slightly once a fallback font swaps
        // for the real one.
        document.fonts?.ready?.then(measure).catch(() => {});
        window.addEventListener("resize", measure);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", measure);
        };
    }, [section, scale]);

    if (section.type === "event") {
        // Admin can hide individual events from THIS section (the event itself
        // stays untouched) and pick grid vs slider layout — both live on the
        // section config.
        const visibleEvents = events.filter((ev: any) => !(section.excludedids || []).includes(ev._id));
        // No events to show: hide the whole section, title/subtitle included —
        // not just the (already self-hiding) EventsSection body below.
        if (!visibleEvents.length) return null;
        return (
            <>
                <SectionCoverImage section={section} />
                <ScrollRevealSection animation={section.sectionAnimation} direction={section.contentDirection} durationSec={section.animationDuration} scrollLinked={!!section.coverImageUrl && section.coverImageEnabled !== false}>
                    <div className="relative w-full" style={{ background: resolveSectionBg(section.bg, pagebg), zIndex: 1 }}>
                        {/* Title/subtitle: real draggable text elements (see
                            defaultTitleSubtitleEls), positioned within a fixed
                            design-space zone — MUST match the editor canvas's own
                            heading-zone height (150px) or dragged positions would
                            read differently live vs. while editing. Background lives
                            on the outer wrapper (above) so it covers the whole
                            section, not just this heading zone. */}
                        <div className="relative w-full" style={{ height: "150px" }}>
                            {section.elements.map((el: any) => <ElementView key={el.id} el={el} />)}
                        </div>
                        <EventsSection
                            events={visibleEvents}
                            displayMode={section.displaymode}
                            slideCols={section.slidecols}
                            cardBg={section.cardbg}
                            showCardTitle={section.showcardtitle !== false}
                            cardTitleColor={section.cardtitlecolor}
                        />
                    </div>
                </ScrollRevealSection>
            </>
        );
    }
    if (section.type === "livevideo") {
        const videoEl = (section.elements || []).find((el: any) => el.kind === "video");
        const videoBox: VideoBox | undefined = videoEl
            ? { x: videoEl.x, y: videoEl.y, width: videoEl.width || 70, height: videoEl.height || 260 }
            : undefined;
        return (
            <>
                <SectionCoverImage section={section} />
                <ScrollRevealSection animation={section.sectionAnimation} direction={section.contentDirection} durationSec={section.animationDuration} scrollLinked={!!section.coverImageUrl && section.coverImageEnabled !== false}>
                    <div className="relative w-full" style={{ background: "#000", zIndex: 1 }}>
                        <LiveVideoSection
                            videos={liveVideos}
                            title={section.title}
                            videoBox={videoBox}
                            sectionHeightPx={height}
                        />
                    </div>
                </ScrollRevealSection>
            </>
        );
    }
    if (section.type === "pagevideo") {
        const visibleVideos = pageVideos.filter((v: any) => !(section.excludedids || []).includes(v._id));
        // No videos to show: hide the whole section, title/subtitle included —
        // not just the (already self-hiding) PageVideosSection body below.
        if (!visibleVideos.length) return null;
        return (
            <>
                <SectionCoverImage section={section} />
                <ScrollRevealSection animation={section.sectionAnimation} direction={section.contentDirection} durationSec={section.animationDuration} scrollLinked={!!section.coverImageUrl && section.coverImageEnabled !== false}>
                    <div className="relative w-full" style={{ background: resolveSectionBg(section.bg, pagebg), zIndex: 1 }}>
                        {/* Title/subtitle: real draggable text elements — see the
                            "event" branch above for why 150px must match the editor
                            canvas's heading-zone height exactly. Background lives on
                            the outer wrapper (above) so it covers the whole section. */}
                        <div className="relative w-full" style={{ height: "150px" }}>
                            {section.elements.map((el: any) => <ElementView key={el.id} el={el} />)}
                        </div>
                        <PageVideosSection
                            videos={visibleVideos}
                            displayMode={section.displaymode}
                            slideCols={section.slidecols}
                            cardBg={section.cardbg}
                            showCardTitle={section.showcardtitle !== false}
                            cardTitleColor={section.cardtitlecolor}
                        />
                    </div>
                </ScrollRevealSection>
            </>
        );
    }
    if (section.type === "slider") {
        return (
            <>
                <SectionCoverImage section={section} />
                <ScrollRevealSection animation={section.sectionAnimation} direction={section.contentDirection} durationSec={section.animationDuration} scrollLinked={!!section.coverImageUrl && section.coverImageEnabled !== false}>
                    <div className="relative w-full" style={{ zIndex: 1 }}>
                        <SliderCarousel slides={section.slides || []} section={section} />
                    </div>
                </ScrollRevealSection>
            </>
        );
    }
    // Header/footer chrome on phone AND tablet: x/y% was tuned for elements
    // sitting side by side at full desktop width — that spacing doesn't
    // translate to a narrower canvas at either size. But not everything should
    // move to its own line: a logo (y:20) sitting next to a title (y:29.8) is
    // one visual row (the small y gap was never meant to read as two rows,
    // just "roughly vertically centered together"), while "Privacy"/"Terms"
    // (y:27/28, essentially identical) are also one row, clearly distinct from
    // "Copyright" (y:45) below them. Cluster elements whose y is within
    // ROW_THRESHOLD of each other into the same row (kept side by side, sorted
    // left to right), and stack only the ROWS vertically — not every element.
    const stackChrome = fixedElementSizes && effectiveWidth < 1024;
    if (stackChrome) {
        // Linked text/buttons the admin marked as menu items collapse into a
        // hamburger dropdown on phone/tablet instead of rendering inline —
        // desktop keeps them at their designed position untouched.
        const menuItems = (section.elements || []).filter((el: any) => el.menuItem && el.link);
        const inlineElements = (section.elements || []).filter((el: any) => !(el.menuItem && el.link));
        const counterZoom = 1 / scale;

        const ROW_THRESHOLD = 15; // percentage points of section height
        const sortedByY = [...inlineElements].sort((a, b) => (a.y || 0) - (b.y || 0));
        const rows: any[][] = [];
        sortedByY.forEach((el) => {
            const currentRow = rows[rows.length - 1];
            if (currentRow && Math.abs((el.y || 0) - (currentRow[0].y || 0)) <= ROW_THRESHOLD) {
                currentRow.push(el);
            } else {
                rows.push([el]);
            }
        });
        rows.forEach((row) => row.sort((a, b) => (a.x || 0) - (b.x || 0)));

        // Keep the admin's own horizontal placement instead of force-centering
        // every row: a logo placed at the LEFT edge of the desktop design (x≈2)
        // should stay left on phone/tablet too — only rows the admin actually
        // positioned around the middle render centered. The row's leftmost x
        // decides: <33% → left, >66% → right, else center.
        // Exception: the FOOTER always centers on phone/tablet — stacked
        // footer rows (Privacy/Terms, copyright) read best centered there.
        const isFooter = section.id === "__footer__";
        const rowJustify = (row: any[]): React.CSSProperties["justifyContent"] => {
            if (isFooter) return "center";
            const firstX = row[0]?.x || 0;
            if (firstX < 33) return "flex-start";
            if (firstX > 66) return "flex-end";
            return "center";
        };

        return (
            <section
                ref={contentSectionRef}
                className="relative w-full flex flex-col justify-center gap-2 py-4 px-4"
                style={{ minHeight: height, background: section.bg || "#ffffff" }}
            >
                {section.bgUrl && <SectionBgImage url={section.bgUrl} fixed={section.bgFixed} />}
                {rows.map((row, i) => (
                    <div key={i} className="flex flex-row items-center gap-2 w-full" style={{ justifyContent: rowJustify(row) }}>
                        {row.map((el: any) => (
                            <ElementView key={el.id} el={el} fixedSize={fixedElementSizes} staticFlow />
                        ))}
                    </div>
                ))}
                {menuItems.length > 0 && (
                    <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, display: "flex", alignItems: "center", zIndex: 30 }}>
                        {/* Counter-zoomed so the hamburger + dropdown render at true,
                            constant pixel sizes regardless of the ambient page zoom */}
                        <span style={{ display: "inline-block", zoom: counterZoom, marginRight: "14px", position: "relative" }}>
                            <button
                                type="button"
                                aria-label="Menu"
                                onClick={toggleMenu}
                                style={{
                                    background: "transparent",
                                    border: "none",
                                    cursor: "pointer",
                                    padding: "8px",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "4px",
                                }}
                            >
                                {menuOpen ? (
                                    <span style={{ fontSize: "20px", lineHeight: "14px", color: "#111827" }}>×</span>
                                ) : (
                                    [0, 1, 2].map((i) => (
                                        <span key={i} style={{ display: "block", width: "20px", height: "2px", background: "#111827", borderRadius: "2px" }} />
                                    ))
                                )}
                            </button>
                        </span>
                    </div>
                )}
                {/* App-style full-height drawer from the right. Rendered via a
                    body PORTAL in plain real pixels (no zoom/transform ancestors
                    to distort fixed positioning); drawerRect confines it to the
                    preview's phone frame, or the full window on the live site. */}
                {menuItems.length > 0 && menuOpen && drawerRect && createPortal(
                    <>
                        {/* Full-window catch layer: clicking anywhere outside the
                            frame closes the drawer, and wheel/touch over it can't
                            reach any scroller (its only ancestor is the locked
                            body) — so nothing behind the drawer ever scrolls. */}
                        <div
                            onClick={() => setMenuOpen(false)}
                            style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 3000 }}
                        />
                        <div
                            onClick={() => setMenuOpen(false)}
                            style={{
                                position: "fixed",
                                top: `${drawerRect.top}px`,
                                left: `${drawerRect.left}px`,
                                width: `${drawerRect.width}px`,
                                height: `${drawerRect.height}px`,
                                background: "rgba(17,24,39,0.45)",
                                borderRadius: `${drawerRect.radius}px`,
                                zIndex: 3001,
                            }}
                        />
                        <div
                            style={{
                                position: "fixed",
                                top: `${drawerRect.top}px`,
                                left: `${drawerRect.left + drawerRect.width - Math.min(300, drawerRect.width * 0.84)}px`,
                                width: `${Math.min(300, drawerRect.width * 0.84)}px`,
                                height: `${drawerRect.height}px`,
                                background: "#ffffff",
                                zIndex: 3002,
                                boxShadow: "-10px 0 30px rgba(0,0,0,0.22)",
                                borderTopRightRadius: `${drawerRect.radius}px`,
                                borderBottomRightRadius: `${drawerRect.radius}px`,
                                display: "flex",
                                flexDirection: "column",
                                // The list scrolls INSIDE the drawer when it outgrows
                                // the height; contain stops the scroll from chaining
                                // to the page when it hits the ends (mobile touch).
                                overflowY: "auto",
                                overscrollBehavior: "contain",
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid #e5e7eb" }}>
                                <span style={{ fontWeight: 700, fontSize: "17px", color: "#264164" }}>Menu</span>
                                <button
                                    type="button"
                                    aria-label="Close menu"
                                    onClick={() => setMenuOpen(false)}
                                    style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: "26px", lineHeight: "20px", color: "#6b7280", padding: "4px" }}
                                >
                                    ×
                                </button>
                            </div>
                            {menuItems.map((item: any) => {
                                const external = /^https?:\/\//i.test(item.link);
                                // Button elements keep their button look inside the
                                // drawer too (their own background color, pill shape)
                                if (item.kind === "button") {
                                    const DrawerBtnIcon = item.icon ? ICON_MAP[item.icon] : null;
                                    const drawerIconNode = DrawerBtnIcon ? <DrawerBtnIcon style={{ width: "17px", height: "17px", flexShrink: 0 }} /> : null;
                                    return (
                                        <div key={item.id} style={{ padding: "12px 20px", borderBottom: "1px solid #f3f4f6" }}>
                                            <a
                                                href={item.link}
                                                target={external ? "_blank" : undefined}
                                                rel={external ? "noreferrer" : undefined}
                                                onClick={() => setMenuOpen(false)}
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    gap: drawerIconNode ? "8px" : undefined,
                                                    padding: "11px 16px",
                                                    background: item.bg || "#22c55e",
                                                    color: item.color || "#ffffff",
                                                    borderRadius: `${item.radius ?? 8}px`,
                                                    fontSize: "15px",
                                                    fontWeight: 600,
                                                    textDecoration: "none",
                                                }}
                                            >
                                                {item.iconPos !== "right" && drawerIconNode}
                                                {item.text || item.link}
                                                {item.iconPos === "right" && drawerIconNode}
                                            </a>
                                        </div>
                                    );
                                }
                                return (
                                    <a
                                        key={item.id}
                                        href={item.link}
                                        target={external ? "_blank" : undefined}
                                        rel={external ? "noreferrer" : undefined}
                                        onClick={() => setMenuOpen(false)}
                                        style={{
                                            display: "block",
                                            padding: "15px 20px",
                                            color: "#264164",
                                            fontSize: "16px",
                                            fontWeight: 600,
                                            textDecoration: "none",
                                            borderBottom: "1px solid #f3f4f6",
                                        }}
                                    >
                                        {item.text || item.link}
                                    </a>
                                );
                            })}
                        </div>
                    </>,
                    document.body
                )}
            </section>
        );
    }

    const declaredHeightPx = parseFloat(height) || 0;
    const resolvedMinHeight = autoHeight != null ? Math.max(declaredHeightPx, autoHeight) : declaredHeightPx;

    // A caption positioned at a fixed y% "below" a photo (e.g. y:84%) only lines
    // up with the photo's actual bottom edge by coincidence — both are really
    // percentages of the WHOLE section, which now correctly grows to fit
    // whatever the tallest, unrelated content elsewhere needs (e.g. a long
    // paragraph). When that stretches the section taller, the caption's 84%
    // moves down with it while the photo (anchored at a fixed y, no taller than
    // its own content) doesn't — opening a gap between them. Detect text
    // sitting directly under an image (same x, greater y) and render them as
    // one flow group (image, then caption stacked right below it via margin)
    // instead of two independent section-relative percentages.
    const allElements = section.elements || [];
    const usedIds = new Set<string>();
    const imageCaptionGroups: { image: any; captions: any[] }[] = [];
    // Header/footer chrome never runs the caption-grouping heuristic below —
    // it uses its own row-based layout (see stackChrome above, and the same
    // logic applies at desktop scale too via ordinary x/y positioning). Without
    // this exclusion, a header's logo+title (image, then text below/near it)
    // matches the exact same geometric pattern as a photo+caption and gets
    // incorrectly forced into a stacked image-then-caption layout.
    if (!fixedElementSizes) {
        allElements
            .filter((el: any) => el.kind === "image")
            .forEach((img: any) => {
                const captions = allElements
                    .filter((el: any) => el.kind === "text" && !usedIds.has(el.id) && Math.abs((img.x || 0) - (el.x || 0)) <= 8 && (el.y || 0) > (img.y || 0))
                    .sort((a: any, b: any) => (a.y || 0) - (b.y || 0));
                if (captions.length) {
                    captions.forEach((c: any) => usedIds.add(c.id));
                    usedIds.add(img.id);
                    imageCaptionGroups.push({ image: img, captions });
                }
            });
    }
    const ungroupedElements = allElements.filter((el: any) => !usedIds.has(el.id));

    // On mobile/tablet, a photo placed beside body text (a side-by-side desktop
    // column layout, e.g. text at x:1%, photo at x:83%) no longer fits without
    // overlapping it: the photo is now a CONSTANT real pixel size (see
    // constantSize above) so it claims far more of a narrow screen's width than
    // its desktop design % ever implied, while the text column still claims its
    // own independent percentage-based width right on top of it. Two absolutely
    // positioned columns can't be reconciled generically — stack them in normal
    // document flow instead (text first, in y order, then the photo+caption
    // centered below): flow layout can't overlap by construction. Desktop/large
    // desktop have real room for the side-by-side design, so they're untouched.
    const stackContent = !fixedElementSizes && effectiveWidth < 1024 && imageCaptionGroups.length > 0;
    if (stackContent) {
        const sortedUngrouped = [...ungroupedElements].sort((a: any, b: any) => (a.y || 0) - (b.y || 0));
        return (
            <section
                className="relative w-full flex flex-col items-start gap-4 py-6 px-4"
                style={{ background: section.bg || "#ffffff" }}
            >
                {section.bgUrl && <SectionBgImage url={section.bgUrl} fixed={section.bgFixed} />}
                {sortedUngrouped.map((el: any) => (
                    <div key={el.id} className="w-full">
                        <ElementView el={el} staticFlow wrapText={el.kind === "text"} />
                    </div>
                ))}
                {imageCaptionGroups.map(({ image, captions }) => (
                    <div key={image.id} className="w-full flex flex-col items-center">
                        {/* No constantSize here: full desktop pixel size swallows most of a
                            narrow screen's width. The breakpoint tier (0.65/0.8) renders it
                            a comfortable medium size on phone/tablet instead — this stacked
                            branch only runs under 1024px, so desktop is unaffected. */}
                        <ElementView el={image} fixedSize staticFlow />
                        {captions.map((c: any) => (
                            <div key={c.id} style={{ marginTop: "8px" }}>
                                <ElementView el={c} fixedSize={fixedElementSizes} staticFlow />
                            </div>
                        ))}
                    </div>
                ))}
            </section>
        );
    }

    return (
        <>
            {/* This same branch also renders the header/footer chrome (via
                fixedElementSizes) — a cover image only makes sense for a real
                content section, not chrome, so it's excluded there. */}
            {!fixedElementSizes && <SectionCoverImage section={section} />}
            {(() => {
                const hasCoverImage = !fixedElementSizes && !!section.coverImageUrl && section.coverImageEnabled !== false;
                // Extra scroll room reserved so the section (once pinned) has
                // somewhere to "spend" scroll on its own reveal before it
                // unsticks and normal scrolling continues to what's next.
                const TRAVEL = 400;
                const bg = section.bg || "#ffffff";
                const sectionEl = (
                    /* minHeight (not height) + overflow stays visible as a safety net for the
                       brief gap before the first measurement lands (see the closed-form
                       solve above resolvedMinHeight). */
                    <section
                        ref={contentSectionRef}
                        className="relative w-full"
                        style={{
                            minHeight: `${resolvedMinHeight}px`,
                            background: bg,
                            zIndex: 1,
                            // +16px so the pinned section doesn't sit flush
                            // against the sticky header's bottom edge.
                            ...(hasCoverImage ? { position: "sticky" as const, top: contentHeaderOffset + 16 } : {}),
                        }}
                    >
                        {section.bgUrl && <SectionBgImage url={section.bgUrl} fixed={section.bgFixed} />}
            {/* Elements reveal one by one (staggered) rather than the whole
                section fading in as one block — same header/footer exclusion
                as the cover image above (chrome is always immediately
                visible, never delayed behind a scroll-reveal). */}
            <StaggeredReveal
                animation={fixedElementSizes ? undefined : section.sectionAnimation}
                direction={fixedElementSizes ? undefined : section.contentDirection}
                durationSec={section.animationDuration}
                scrollLinked={hasCoverImage}
                sticky={hasCoverImage}
                stickyOffset={contentHeaderOffset + 16}
                sectionRef={contentSectionRef}
            >
            {imageCaptionGroups.map(({ image, captions }) => {
                // fixedSize on the image (regardless of section type): the same
                // counter-zoom mechanism used for header/footer chrome, so this
                // photo renders at one constant REAL pixel size on any screen —
                // never bigger, never smaller. But its position (left%) still
                // tracks the zoomed layout like everything else — so a photo
                // positioned at x:83% for a 1440px desktop canvas, kept at its
                // full constant width while the canvas shrinks to a ~390px
                // phone, no longer has anywhere near enough real room to its
                // right and runs off the edge of the actual screen (clipped by
                // the device/preview frame). Recompute its left% (only when
                // actually zoomed down, scale<1) so the constant-sized photo
                // lands right-aligned with a small real margin from the
                // device's actual right edge instead of overflowing it.
                const photoWidthPx = ((image.width || 30) / 100) * 1440;
                const marginPx = 16;
                const groupLeft = scale < 1
                    ? Math.max(2, ((effectiveWidth - photoWidthPx - marginPx) / effectiveWidth) * 100)
                    : (image.x || 0);
                return (
                <div
                    key={image.id}
                    data-el-item
                    data-el-y={image.y || 0}
                    style={{ position: "absolute", left: `${groupLeft}%`, top: `${image.y}%`, display: "flex", flexDirection: "column", alignItems: "flex-start" }}
                >
                    <ElementView el={image} fixedSize constantSize staticFlow />
                    {captions.map((c: any) => (
                        <div key={c.id} style={{ marginTop: "8px" }}>
                            <ElementView el={c} fixedSize={fixedElementSizes} staticFlow />
                        </div>
                    ))}
                </div>
                );
            })}
            {ungroupedElements.map((el: any) => (
                <ElementView key={el.id} el={el} fixedSize={fixedElementSizes} />
            ))}
            </StaggeredReveal>
                    </section>
                );
                // Extra-tall wrapper gives the now-sticky section room to stay
                // pinned (and finish its reveal) before scrolling releases it
                // to whatever comes next — without this it would have nowhere
                // to "spend" scroll and would unstick almost immediately.
                return hasCoverImage
                    ? <div style={{ minHeight: `${resolvedMinHeight + TRAVEL}px` }}>{sectionEl}</div>
                    : sectionEl;
            })()}
        </>
    );
};

// Header/footer preview strips shared by the public page and the admin preview
export const SiteHeaderBar: React.FC<{ header: any; logo?: string; children?: React.ReactNode }> = ({ header, logo, children }) => {
    const { effectiveWidth } = useResponsiveScale();

    const logoSize =
        effectiveWidth < 480
            ? 26
            : effectiveWidth < 768
                ? 32
                : 40;

    const titleSize =
        effectiveWidth < 480
            ? 18
            : effectiveWidth < 768
                ? 20
                : 24;
    const stacked = effectiveWidth < 480 && !!children;
    return (
        <nav
            // Stickiness is the caller's call, not this component's — see
            // HomeContentView.tsx's isSticky wrapper (admin-configurable via
            // header.sticky) vs. the admin editor's own decorative preview
            // renders of this component, which stay non-sticky on purpose.
            className="shadow-md p-5"
            style={{
                background: header?.bg || "#ffffff",
                color: header?.color || "#111827",
                padding:
                    effectiveWidth < 480
                        ? "10px 14px"
                        : effectiveWidth < 768
                            ? "14px 18px"
                            : "20px 24px"
            }}
        >
            <div
                className="flex items-center gap-2"
                style={{ background: "transparent", flexDirection: stacked ? "column" : "row", justifyContent: stacked ? "flex-start" : "space-between", alignItems: stacked ? "flex-start" : "center" }}
            >
                <div className="flex items-center">
                    {logo && <img src={logo} alt={header?.title || "Sagar Samaj"} style={{ height: logoSize, width: "auto", display: "block" }} />}
                    <span className="ml-1 font-semibold" style={{ fontSize: titleSize }}>{header?.title || "Sagar Samaj"}</span>
                </div>
                {children}
            </div>
        </nav>
    );
};

// Deliberately no Tailwind sm:/md:/lg: breakpoint classes here. The builder's
// "mobile view" is produced by zoom-scaling the whole page down to fit — it
// evaluates layout at the true (unscaled) design width always, so a breakpoint
// class here would read the real browser viewport (wide in the admin's preview,
// narrow on an actual phone) and render two different arrangements depending on
// who's looking. A single width-independent layout keeps editor, preview, and
// live mobile identical.
export const SiteFooterBar: React.FC<{ footer: any; children?: React.ReactNode }> = ({ footer, children }) => {
    const { effectiveWidth } = useResponsiveScale();

    const textSize = useMinFontSize(16, 13);
    const mobile = effectiveWidth < 640;
    // Always stacked (column) — row layout kept overlapping the links and the
    // copyright text at tablet/mobile widths no matter where the threshold was
    // set. Height is naturally auto here (no fixed height anywhere in the
    // footer), so stacking just makes it taller instead of clipping anything.
    // Padding also tiers down — a fixed 32px/40px (py-8 px-10) desktop padding
    // reads as oversized empty space once the footer itself is much narrower.
    const footerPadding = effectiveWidth < 640 ? "16px" : effectiveWidth < 1024 ? "24px" : "32px";
    return (
        <footer
            style={{ padding: footerPadding, background: footer?.bg || "#000000", color: footer?.color || "#9ca3af" }}
        >
            <div className="max-w-7xl mx-auto flex text-left gap-2"
                style={{
                    display: "flex",
                    flexDirection: mobile ? "column" : "row",
                    justifyContent: "space-between",
                    alignItems: mobile ? "center" : "center",
                    gap: mobile ? 12 : 0,
                    textAlign: mobile ? "center" : "left"
                }}
            >
                {children}
                <div>
                    <p className="m-0" style={{ fontSize: textSize }}>
                        {footer?.text || `Copyright © ${new Date().getFullYear()} RMD Import Export Company. All rights reserved.`}
                    </p>
                </div>
            </div>
        </footer>
    );
};
